import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import firebaseApp, { db, auth, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  addDoc,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  arrayUnion,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import { getPodColor, assignPodColors } from "./podColors";
import { formatTime, localDateKey, getInitials, getEventKind, normEmail } from "./dateUtils";
import Dashboard from "./Dashboard";
import EventForm from "./EventForm";
import Pods, { Dialog, makeNamer } from "./Pods";
import Contacts from "./Contacts";
import Welcome from "./Welcome";
import { ScheduleForm } from "./Schedules";
import { takesRsvp, rsvpNotifFields, sendRsvp, RsvpPods, RsvpAsk } from "./rsvp";
import { changeNotifs, cancelNotifs } from "./planChanges";
import { expandRepeats, cleanRepeat, repeatLabel, addDaysKey, toGoogleRecurrence, occurrenceDates } from "./repeat";

// Every week (Sun–Sat) from the one holding `from` to the one holding `to`, for the scrolling month view.
function weeksBetween(from, to) {
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate() - from.getDay());
  const weeks = [];
  while (cur <= to) {
    const week = [];
    for (let d = 0; d < 7; d++) { week.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    weeks.push(week);
  }
  return weeks;
}

const todayKey = localDateKey(new Date());

const GRID_START = 6;   // 6 AM
const GRID_END   = 22;  // 10 PM
const SLOT_H     = 64;  // px per hour

function getWeekDays(anchorDate) {
  const d = new Date(anchorDate);
  d.setDate(d.getDate() - d.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    return day;
  });
}

function formatHour(h) {
  if (h === 0)  return "12 AM";
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

function eventTop(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  return ((h * 60 + m) - GRID_START * 60) * SLOT_H / 60;
}

function eventHeight(startStr, endStr) {
  if (!startStr) return SLOT_H;
  const [sh, sm] = startStr.split(":").map(Number);
  const [eh, em] = (endStr || startStr).split(":").map(Number);
  const dur = (eh * 60 + em) - (sh * 60 + sm);
  return Math.max(dur * SLOT_H / 60, 24);
}

// ── Google Calendar write-back ──
// Copies go to a secondary calendar the app creates (calendar.app.created scope), so the app
// can never touch the user's own calendars. The calendar is found by GCAL_MARKER in its description (people can
// see it in Google Calendar, so it's "frisoapp.com"; calendars made before 2026-10-12 carry the Firebase project id
// and get the new description on next load), so renaming APP_NAME renames the calendar instead of making a new one.
// Each copy carries the app event id in a private extended property; only those are ever patched/deleted.
export const APP_NAME = "FriSo";
const GCAL_MARKER = "(frisoapp.com)";
const GCAL_LEGACY_MARKER = firebaseApp.options.projectId;
const isAppCalendar = (c) => (c.description || "").includes(GCAL_MARKER) || (c.description || "").includes(GCAL_LEGACY_MARKER);
const GCAL_WRITE_SCOPE = "https://www.googleapis.com/auth/calendar.app.created";
const GCAL_API = "https://www.googleapis.com/calendar/v3";

async function gcal(token, path, { method = "GET", body } = {}) {
  const res = await fetch(GCAL_API + path, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body && { "Content-Type": "application/json" }) },
    body: body && JSON.stringify(body),
  });
  if (!res.ok) {
    const err = new Error(`Google Calendar responded ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? null : res.json();
}

// Returns the app calendar's id, or null if it doesn't exist and create is false.
async function findAppCalendar(token, create) {
  const description = `Events synced from ${APP_NAME} ${GCAL_MARKER}`;
  const list = await gcal(token, "/users/me/calendarList?minAccessRole=owner&maxResults=250");
  const existing = (list.items || []).find(isAppCalendar);
  if (existing) {
    if (existing.summary !== APP_NAME || existing.description !== description) {
      await gcal(token, `/calendars/${encodeURIComponent(existing.id)}`, { method: "PATCH", body: { summary: APP_NAME, description } });
    }
    return existing.id;
  }
  if (!create) return null;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (await gcal(token, "/calendars", { method: "POST", body: { summary: APP_NAME, description, timeZone } })).id;
}

// Copy deletions wait until all 5 event/pod/private-details snapshots have arrived, so a half-loaded app never
// looks like "event gone" (and Google copies never get the "Busy" placeholder title).
function markLoaded(ref, setReady, name) {
  ref.current.add(name);
  if (ref.current.size === 5) setReady(true);
}

// Google event ids in the app: "__google__<itemId>" for the primary calendar (the original format, kept so
// older shared copies still match) and "__google__<calendarId>:<itemId>" for other calendars (item ids are only
// unique within a calendar). Item ids never contain ":", so the last ":" splits them.
const GOOGLE_PREFIX = "__google__";
const googleEventKey = (calId, itemId, isPrimary) => `${GOOGLE_PREFIX}${isPrimary ? "" : calId + ":"}${itemId}`;
const calendarOfGoogleKey = (key) => {
  const rest = key.slice(GOOGLE_PREFIX.length);
  const i = rest.lastIndexOf(":");
  return i < 0 ? "primary" : rest.slice(0, i);
};

// Which calendars to show: the saved picks, else the ones switched on in Google Calendar itself (else the main
// calendar). Saved picks that don't exist in the connected Google account (e.g. after connecting a different one)
// count as no picks. An empty saved list means the user switched every calendar off, and that's respected.
const pickedCalendarIds = (saved, list) => {
  if (saved && (saved.length === 0 || saved.some((id) => list.some((c) => c.id === id)))) return saved;
  const onInGoogle = list.filter((c) => c.selected).map((c) => c.id); // what they see in Google Calendar itself
  return onInGoogle.length ? onInGoogle : list.filter((c) => c.primary).map((c) => c.id);
};

// Busy events are private: pods get this placeholder, and the real title/location/notes live in
// eventPrivate/{eventId}, which only the owner can read (firestore.rules). See CLAUDE.md "Busy = private".
const BUSY_PUBLIC = { title: "Busy", location: null, notes: null, htmlLink: null, eventType: null };

function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return String(h >>> 0);
}

// App event → Google event body. Untimed events become all-day; a missing (or earlier) end time means 1 hour
// (or the rest of the end date, for a timed event that spans days).
function toGoogleEvent(ev) {
  const kind = ev.eventKind || "event";
  const endDate = ev.endDate || ev.startDate;
  let start, end;
  if (ev.startTime) {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const s = new Date(`${ev.startDate}T${ev.startTime}`);
    let e = ev.endTime ? new Date(`${endDate}T${ev.endTime}`) : null;
    // Several days with no end time (e.g. a trip that starts in the evening): runs through the end date
    if (!e && endDate > ev.startDate) { e = new Date(`${endDate}T00:00`); e.setDate(e.getDate() + 1); }
    if (!e || e <= s) e = new Date(s.getTime() + 60 * 60 * 1000);
    const fmt = (d) => `${localDateKey(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`;
    start = { dateTime: fmt(s), timeZone };
    end = { dateTime: fmt(e), timeZone };
  } else {
    const d = new Date(endDate + "T00:00");
    d.setDate(d.getDate() + 1); // Google all-day end dates are exclusive
    start = { date: ev.startDate };
    end = { date: localDateKey(d) };
  }
  if (ev.repeat) {
    // Google counts the start as an occurrence even if it isn't one of the repeat days: start on the first real one
    const first = occurrenceDates({ ...ev, repeatSkip: [] }, ev.startDate, addDaysKey(ev.startDate, 400))[0];
    const shift = first && first !== ev.startDate ? Math.round((new Date(first + "T00:00") - new Date(ev.startDate + "T00:00")) / 86400000) : 0;
    if (shift) return toGoogleEvent({ ...ev, startDate: first, endDate: addDaysKey(endDate, shift) });
  }
  const fields = {
    summary: ev.title || (kind === "busy" ? "Busy" : kind === "traveling" ? "Traveling" : "(No title)"),
    description: (ev.notes ? ev.notes + "\n\n" : "") + `Added from ${APP_NAME}`,
    location: ev.location || "",
    start,
    end,
    ...(ev.repeat && { recurrence: toGoogleRecurrence(ev, Intl.DateTimeFormat().resolvedOptions().timeZone) }),
  };
  return { ...fields, extendedProperties: { private: { appEventId: ev.id, appHash: hashString(JSON.stringify(fields)) } } };
}

export default function Calendar({
  view = "calendar", googleAccessToken = null, googleConnected = null, googleConnecting = false, onGoogleTokenExpired, onReconnectGoogle,
  pendingInvitations, onAcceptInvitation, onDeclineInvitation, onNavigate, onOpenNote,
  eventIntent = null, podIntent = null, onIntentDone = () => {},
}) {
  const [events, setEvents] = useState({});
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleCalendars, setGoogleCalendars] = useState([]); // [{ id, name, color, primary }] from the user's calendar list
  const [podColorPicks, setPodColorPicks] = useState(undefined); // userSettings podColors { podId: slot }; undefined = loading
  const [shownCalendars, setShownCalendars] = useState(undefined); // ids picked in "Google calendars"; null = never picked (main only); undefined = loading
  const [calPickerOpen, setCalPickerOpen] = useState(false);
  const [calPickerExpanded, setCalPickerExpanded] = useState(null); // calendar whose still-shared events are listed
  const [onboardedAt, setOnboardedAt] = useState(undefined); // userSettings onboardedAt; null = never finished the welcome; undefined = loading
  const [calBannerSeen, setCalBannerSeen] = useState(undefined); // userSettings calendarBannerSeen; undefined = loading
  const [calBannerNow, setCalBannerNow] = useState(false); // the one visit the "You're seeing N of M" banner shows on
  const [welcomeDone, setWelcomeDone] = useState(false);
  const [title, setTitle] = useState("");
  const [startDateValue, setStartDateValue] = useState(() => localDateKey(new Date()));
  const [endDateValue, setEndDateValue] = useState(() => localDateKey(new Date()));
  const [startTimeValue, setStartTimeValue] = useState("");
  const [endTimeValue, setEndTimeValue] = useState("");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState("");
  const [selectedEventPods, setSelectedEventPods] = useState([]);
  const [current, setCurrent] = useState(() => new Date());
  const [monthRange, setMonthRange] = useState({ from: -6, to: 12 }); // scrolling month view, in months from now
  const monthScrollRef = useRef(null);
  const monthScrollTo = useRef("month"); // after render: "month" = top of current's month, a date key = that week, null = stay
  const monthPrevHeight = useRef(null); // scrollHeight before prepending months, to keep the view still
  const [monthRowH, setMonthRowH] = useState(0); // desktop: week row height that fits a 6-week month in the pane
  const [calView, setCalView] = useState("month");
  const [selectedDay, setSelectedDay] = useState(() => localDateKey(new Date()));
  const [daySheetOpen, setDaySheetOpen] = useState(false); // phone: the tapped day's plans in a bottom sheet
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewAnchorRef = useRef(null); // month cell the desktop day popover sits beside
  const previewRef = useRef(null);
  const eventAnchorRef = useRef(null); // { el, rect } of what was clicked to open the desktop event popover
  const eventPopRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editStartDateValue, setEditStartDateValue] = useState("");
  const [editEndDateValue, setEditEndDateValue] = useState("");
  const [editStartTimeValue, setEditStartTimeValue] = useState("");
  const [editEndTimeValue, setEditEndTimeValue] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editEventPods, setEditEventPods] = useState([]);
  const [editEventKind, setEditEventKind] = useState("event"); // "event"|"busy"|"traveling"
  // Add event form
  const [eventKind, setEventKind] = useState("event"); // "event"|"busy"|"traveling"
  const [pods, setPods] = useState({});
  const [contacts, setContacts] = useState({});
  const [invitations, setInvitations] = useState([]);
  const [googleShareKind, setGoogleShareKind] = useState("busy");
  const [googleSharePods, setGoogleSharePods] = useState([]);
  const [showGoogleShareForm, setShowGoogleShareForm] = useState(false);
  const [allEventsFlat, setAllEventsFlat] = useState([]); // one entry per events/ doc (a repeating event once)
  const [displayEvents, setDisplayEvents] = useState([]); // allEventsFlat with repeating events expanded (repeat.js)
  const [repeat, setRepeat] = useState(null); // add form: { freq, days, until } or null
  const [editRepeat, setEditRepeat] = useState(null);
  const [repeatScope, setRepeatScope] = useState(null); // "save" | "delete": asking which occurrences
  const [scheduleDraft, setScheduleDraft] = useState(null); // Schedules form: {} = new, else the schedule's event doc
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, dateKey, startTime?, endTime? }
  const [evMenuOpen, setEvMenuOpen] = useState(false); // ⋯ menu on someone else's event (Hide from my calendar)
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [calHidden, setCalHidden] = useState([]); // calendar filter chips: hidden podIds, "me", "google"
  const [showSchedules, setShowSchedules] = useState(false); // "Schedules" chip: off = schedules stay out of the calendar
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  const [quickLoading, setQuickLoading] = useState(false);
  const [googleCanWrite, setGoogleCanWrite] = useState(null); // null = checking, false = needs reconnect
  const [googleCopies, setGoogleCopies] = useState(null); // { appEventId: { id, hash } }; null = not loaded
  const [syncToGoogle, setSyncToGoogle] = useState(true);
  const [dataReady, setDataReady] = useState(false); // events + pods snapshots have all arrived once
  const appCalendarIdRef = useRef(null);
  const syncingRef = useRef(new Set());
  const loadedSnapshotsRef = useRef(new Set());
  const allEventsFlatRef = useRef([]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => { allEventsFlatRef.current = allEventsFlat; }, [allEventsFlat]);

  // Use the authenticated user directly — never trust a user-editable field for identity checks.
  const currentUser = auth.currentUser;
  const currentUserEmail = currentUser?.email ?? "";
  const currentUserUid = currentUser?.uid ?? "";

  useEffect(() => {
    if (!currentUserUid || !currentUserEmail) return;

    // Two queries are needed because Firestore doesn't support OR across different fields.
    // ownedEvs: events this user created (catches legacy events without memberEmails).
    // sharedEvs: events explicitly shared with this user via memberEmails.
    const ownedEvs = {};
    const sharedEvs = {};
    const privateDetails = {}; // eventId → { title, location, notes } for the owner's Busy events

    const buildAndSetEvents = () => {
      const combined = { ...sharedEvs, ...ownedEvs }; // owned takes precedence on conflict
      const map = {};
      const flat = [];
      Object.values(combined).forEach((raw) => {
        // Owner's Busy events: show the real details (pods only ever see the "Busy" placeholder)
        const p = privateDetails[raw.id];
        const ev = p && raw.ownerUid === currentUserUid
          ? { ...raw, title: p.title, location: p.location ?? null, notes: p.notes ?? null, htmlLink: p.htmlLink ?? raw.htmlLink ?? null, eventType: p.eventType ?? raw.eventType ?? null, _private: true }
          : raw;
        if (!ev.startDate) return; // pre-2026 legacy format (date/time/podId) is no longer supported
        flat.push(ev);
      });
      // Repeating events become one entry per date, 1 year back to 2 years ahead
      const now = new Date();
      const shown = expandRepeats(flat, localDateKey(new Date(now.getFullYear() - 1, now.getMonth(), 1)), localDateKey(new Date(now.getFullYear() + 2, now.getMonth(), 1)));
      shown.forEach((ev) => {
        let cur = new Date(ev.startDate + "T00:00");
        const endD = new Date((ev.endDate || ev.startDate) + "T00:00");
        while (cur <= endD) {
          const key = localDateKey(cur);
          if (!map[key]) map[key] = [];
          map[key].push(ev);
          cur.setDate(cur.getDate() + 1);
        }
      });
      setAllEventsFlat(flat);
      setDisplayEvents(shown);
      setEvents(map);
    };

    const q1 = query(collection(db, "events"), where("ownerUid", "==", currentUserUid));
    const unsub1 = onSnapshot(q1, (snap) => {
      Object.keys(ownedEvs).forEach((k) => delete ownedEvs[k]);
      snap.docs.forEach((d) => { ownedEvs[d.id] = { id: d.id, ...d.data() }; });
      buildAndSetEvents();
      markLoaded(loadedSnapshotsRef, setDataReady, "ownedEvents");
    });

    const q2 = query(collection(db, "events"), where("memberEmails", "array-contains", currentUserEmail));
    const unsub2 = onSnapshot(q2, (snap) => {
      Object.keys(sharedEvs).forEach((k) => delete sharedEvs[k]);
      snap.docs.forEach((d) => { sharedEvs[d.id] = { id: d.id, ...d.data() }; });
      buildAndSetEvents();
      markLoaded(loadedSnapshotsRef, setDataReady, "sharedEvents");
    });

    const q3 = query(collection(db, "eventPrivate"), where("ownerUid", "==", currentUserUid));
    const unsub3 = onSnapshot(q3, (snap) => {
      Object.keys(privateDetails).forEach((k) => delete privateDetails[k]);
      snap.docs.forEach((d) => { privateDetails[d.id] = d.data(); });
      buildAndSetEvents();
      markLoaded(loadedSnapshotsRef, setDataReady, "privateDetails");
    }, (err) => {
      console.error("eventPrivate subscription failed", err);
      markLoaded(loadedSnapshotsRef, setDataReady, "privateDetails");
    });

    return () => { unsub1(); unsub2(); unsub3(); };
  }, [currentUserUid, currentUserEmail]);

  useEffect(() => {
    if (!currentUserUid || !currentUserEmail) return;

    // Two queries for pods: ones the user owns, and ones they're a member of.
    const ownedPods = {};
    const memberPods = {};
    const mergePods = () => setPods({ ...memberPods, ...ownedPods });

    const qPods1 = query(collection(db, "pods"), where("ownerUid", "==", currentUserUid));
    const unsubPods1 = onSnapshot(qPods1, (snap) => {
      Object.keys(ownedPods).forEach((k) => delete ownedPods[k]);
      snap.docs.forEach((d) => { ownedPods[d.id] = { id: d.id, ...d.data() }; });
      mergePods();
      markLoaded(loadedSnapshotsRef, setDataReady, "ownedPods");
    });

    const qPods2 = query(collection(db, "pods"), where("members", "array-contains", currentUserEmail));
    const unsubPods2 = onSnapshot(qPods2, (snap) => {
      Object.keys(memberPods).forEach((k) => delete memberPods[k]);
      snap.docs.forEach((d) => { memberPods[d.id] = { id: d.id, ...d.data() }; });
      mergePods();
      markLoaded(loadedSnapshotsRef, setDataReady, "memberPods");
    });

    // Contacts are strictly private — filter by ownerUid server-side.
    const qContacts = query(collection(db, "contacts"), where("ownerUid", "==", currentUserUid));
    const unsubContacts = onSnapshot(qContacts, (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = { id: d.id, ...d.data() }; });
      setContacts(map);
    });

    return () => { unsubPods1(); unsubPods2(); unsubContacts(); };
  }, [currentUserUid, currentUserEmail]);

  useEffect(() => {
    if (!currentUserEmail) return;
    const q = query(collection(db, "invitations"), where("inviterEmail", "==", currentUserEmail));
    const unsub = onSnapshot(q, (snap) => {
      setInvitations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [currentUserEmail]);

  // Per-user settings: which Google calendars to show (userSettings/{uid}, owner-only)
  useEffect(() => {
    if (!currentUserUid) return;
    return onSnapshot(doc(db, "userSettings", currentUserUid),
      (snap) => {
        const d = snap.data() || {};
        setShownCalendars(d.googleCalendars ?? null);
        setPodColorPicks(d.podColors || {});
        setOnboardedAt(d.onboardedAt ?? null);
        setCalBannerSeen(!!d.calendarBannerSeen);
        // Time zone, so replies to texts (Text FriSo) use this person's dates and times
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && snap.exists() && d.timeZone !== tz) setDoc(snap.ref, { timeZone: tz }, { merge: true }).catch(() => {});
      },
      (err) => { console.error("userSettings read failed", err); setShownCalendars(null); setPodColorPicks({}); setOnboardedAt(0); setCalBannerSeen(true); });
  }, [currentUserUid]);

  // This user's pod colors (podColors.js): saved picks, else the next unused color, oldest pod first. Newly
  // chosen colors are saved once everything has loaded, so they stay put when pods are added or removed.
  const freshPodColorsKey = JSON.stringify(assignPodColors(Object.values(pods), podColorPicks || {}));
  useEffect(() => {
    if (!dataReady || podColorPicks === undefined || freshPodColorsKey === "{}") return;
    setDoc(doc(db, "userSettings", currentUserUid), { podColors: JSON.parse(freshPodColorsKey) }, { merge: true })
      .catch((err) => console.error("Couldn't save pod colors", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, podColorPicks === undefined, freshPodColorsKey]);

  const pickPodColor = (podId, slot) => {
    setPodColorPicks((prev) => ({ ...(prev || {}), [podId]: slot }));
    setDoc(doc(db, "userSettings", currentUserUid), { podColors: { [podId]: slot } }, { merge: true })
      .catch((err) => window.alert("Couldn't save the color: " + err.message));
  };
  const shownCalendarsKey = shownCalendars === undefined ? "loading" : (shownCalendars || ["(main)"]).join(",");

  // Fetch Google Calendar events when an access token is available
  useEffect(() => {
    if (!googleAccessToken) { setGoogleEvents([]); setGoogleCalendars([]); return; }
    if (shownCalendars === undefined) return; // wait for the user's calendar choice

    const fetchGoogleEvents = async () => {
      const now = new Date();
      const minDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const maxDate = new Date(now.getFullYear(), now.getMonth() + 6, 1);
      const headers = { Authorization: `Bearer ${googleAccessToken}` };

      try {
        // Every page of every shown calendar; any error aborts so the twin sync below never runs on partial data.
        const getAll = async (url, key) => {
          const out = [];
          let pageToken = "";
          do {
            const res = await fetch(url + (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""), { headers });
            if (res.status === 401) { const e = new Error("expired"); e.expired = true; throw e; }
            if (!res.ok) throw new Error(`Google Calendar responded ${res.status}`);
            const data = await res.json();
            out.push(...(data[key] || []));
            pageToken = data.nextPageToken;
          } while (pageToken);
          return out;
        };

        // The user's calendars, minus the FriSo calendar (copies would come back in as duplicates)
        const list = (await getAll("https://www.googleapis.com/calendar/v3/users/me/calendarList?maxResults=250", "items"))
          .filter((c) => !isAppCalendar(c))
          .map((c) => ({ id: c.id, name: c.summaryOverride || c.summary || c.id, color: c.backgroundColor || null, primary: !!c.primary, selected: !!c.selected }))
          .sort((a, b) => (b.primary - a.primary) || a.name.localeCompare(b.name));
        setGoogleCalendars(list);
        const wanted = pickedCalendarIds(shownCalendars, list);
        // Hidden calendars you've shared events from are still read, so those shared copies keep syncing
        const sharedFrom = new Set(allEventsFlatRef.current
          .filter((ev) => ev.googleEventId && ev.ownerUid === currentUserUid)
          .map((ev) => calendarOfGoogleKey(ev.googleEventId)));
        const toFetch = list.filter((c) => wanted.includes(c.id) || sharedFrom.has(c.primary ? "primary" : c.id));

        const items = [];
        for (const cal of toFetch) {
          const calItems = await getAll(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(cal.primary ? "primary" : cal.id)}/events` +
            `?timeMin=${encodeURIComponent(minDate.toISOString())}&timeMax=${encodeURIComponent(maxDate.toISOString())}` +
            `&maxResults=2500&singleEvents=true&orderBy=startTime` +
            `&timeZone=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`, "items");
          calItems.forEach((item) => items.push({ item, cal }));
        }
        const fetchedCals = new Set(toFetch.map((c) => (c.primary ? "primary" : c.id)));

        const parsed = items.map(({ item, cal }) => {
          const isAllDay = !!item.start?.date;
          const startDate = isAllDay
            ? item.start.date
            : item.start?.dateTime?.slice(0, 10);
          // Google all-day end dates are exclusive — subtract one day
          const endDate = isAllDay
            ? (() => {
                const d = new Date(item.end.date + "T00:00:00");
                d.setDate(d.getDate() - 1);
                return localDateKey(d);
              })()
            : item.end?.dateTime?.slice(0, 10);
          const startTime = isAllDay ? null : item.start?.dateTime?.slice(11, 16);
          const endTime = isAllDay ? null : item.end?.dateTime?.slice(11, 16);

          return {
            id: googleEventKey(cal.id, item.id, cal.primary),
            _calendarId: cal.id,
            _calendarName: cal.name,
            _calendarColor: cal.color,
            title: item.summary || "(No title)",
            startDate,
            endDate: endDate || startDate,
            startTime,
            endTime,
            notes: (() => {
              // fromGmail events only have boilerplate in description — skip it
              if (!item.description || item.eventType === "fromGmail") return null;
              const html = item.description
                .replace(/<br\s*\/?>/gi, "\n")
                .replace(/<\/?(p|div|tr|li|h[1-6])[^>]*>/gi, "\n");
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const text = doc.body.textContent || "";
              const cleaned = text
                .split("\n")
                .map((line) => line.trim())
                .filter((l) => {
                  return (
                    l &&
                    !l.includes("g.co/calendar") &&
                    !l.includes("mail.google.com/mail?extsrc=cal") &&
                    !l.startsWith("To see detailed information") &&
                    !l.startsWith("This event was created from an email")
                  );
                })
                .join("\n")
                .trim();
              return cleaned || null;
            })(),
            location: item.location || null,
            htmlLink: item.htmlLink || null,
            eventType: item.eventType || null,
            sharedPods: [],
            ownerUid: currentUserUid,
            ownerEmail: currentUserEmail,
            _source: "google",
          };
        });

        setGoogleEvents(parsed.filter((ev) => wanted.includes(ev._calendarId))); // only shown calendars appear

        // Sync Firestore twins for previously shared Google events.
        // googleEventId on a Firestore event equals the Google event's app id (googleEventKey).
        const googleIdMap = new Map(parsed.map((ev) => [ev.id, ev]));
        const sharedGoogleEvs = allEventsFlatRef.current.filter(
          (ev) => ev.googleEventId && (ev.ownerUid === currentUserUid || ev.ownerEmail === currentUserEmail)
        );
        await Promise.all(sharedGoogleEvs.map(async (firestoreEv) => {
          const googleEv = googleIdMap.get(firestoreEv.googleEventId);
          // A calendar that wasn't fetched (switched off, or removed from Google) says nothing about its events
          if (!fetchedCals.has(calendarOfGoogleKey(firestoreEv.googleEventId))) return;
          if (!googleEv) {
            // Only treat it as deleted in Google if it falls inside the window we fetched.
            const d = firestoreEv.startDate;
            if (d && d >= localDateKey(minDate) && d < localDateKey(maxDate)) {
              await deleteDoc(doc(db, "events", firestoreEv.id));
            }
            return;
          }
          const changed =
            firestoreEv.title !== googleEv.title ||
            firestoreEv.startDate !== googleEv.startDate ||
            firestoreEv.endDate !== googleEv.endDate ||
            firestoreEv.startTime !== googleEv.startTime ||
            firestoreEv.endTime !== googleEv.endTime ||
            firestoreEv.notes !== googleEv.notes ||
            firestoreEv.location !== googleEv.location;
          if (changed) {
            const when = {
              startDate: googleEv.startDate,
              endDate: googleEv.endDate,
              startTime: googleEv.startTime,
              endTime: googleEv.endTime,
              updatedAt: Date.now(),
            };
            const details = { title: googleEv.title, notes: googleEv.notes, location: googleEv.location };
            if (getEventKind(firestoreEv) === "busy") {
              const batch = writeBatch(db);
              batch.update(doc(db, "events", firestoreEv.id), { ...when, ...BUSY_PUBLIC });
              batch.set(doc(db, "eventPrivate", firestoreEv.id), { ownerUid: currentUserUid, ...details }, { merge: true });
              await batch.commit();
            } else {
              await updateDoc(doc(db, "events", firestoreEv.id), { ...when, ...details });
            }
          }
        }));
      } catch (err) {
        if (err.expired) { onGoogleTokenExpired?.(); return; }
        console.error("Google Calendar fetch failed:", err);
      }
    };

    fetchGoogleEvents();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchGoogleEvents();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
    // onGoogleTokenExpired is an inline prop from App (new every render) — including it would refetch on every render.
    // shownCalendars is covered by shownCalendarsKey (a new array arrives on every settings snapshot).
    // dataReady: the first fetch can run before events load; fetch again once shared copies are known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleAccessToken, currentUserUid, currentUserEmail, shownCalendarsKey, dataReady]); // dataReady: refetch once shared copies are known

  // Check the token can write, then load every copy in the app calendar (no date window — it only holds copies).
  useEffect(() => {
    setGoogleCanWrite(null);
    setGoogleCopies(null);
    appCalendarIdRef.current = null;
    if (!googleAccessToken) return;
    let cancelled = false;

    const loadCopies = async () => {
      try {
        const info = await fetch(`https://oauth2.googleapis.com/tokeninfo?access_token=${encodeURIComponent(googleAccessToken)}`).then((r) => r.json());
        if (!(info.scope || "").split(" ").includes(GCAL_WRITE_SCOPE)) {
          if (!cancelled) setGoogleCanWrite(false);
          return;
        }
        if (!appCalendarIdRef.current) appCalendarIdRef.current = await findAppCalendar(googleAccessToken, false);
        const calId = appCalendarIdRef.current;
        const copies = {};
        if (calId) {
          let pageToken = "";
          do {
            const data = await gcal(googleAccessToken,
              `/calendars/${encodeURIComponent(calId)}/events?maxResults=2500` + (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""));
            for (const item of data.items || []) {
              const props = item.extendedProperties?.private;
              if (!props?.appEventId) continue;
              if (copies[props.appEventId]) {
                // Duplicate copy (e.g. added from two devices at once) — keep the first.
                await gcal(googleAccessToken, `/calendars/${encodeURIComponent(calId)}/events/${encodeURIComponent(item.id)}`, { method: "DELETE" });
              } else {
                copies[props.appEventId] = { id: item.id, hash: props.appHash };
              }
            }
            pageToken = data.nextPageToken;
          } while (pageToken);
        }
        if (!cancelled) {
          setGoogleCanWrite(true);
          setGoogleCopies(copies);
        }
      } catch (err) {
        console.error("Google Calendar copies load failed:", err);
      }
    };

    loadCopies();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") loadCopies();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [googleAccessToken]);

  // New-event form: "Sync to Google Calendar" starts checked every time it opens.
  useEffect(() => {
    if (showAddEventModal) setSyncToGoogle(true);
  }, [showAddEventModal]);

  // Natural-language quick-add: Claude fills the add-event form; the user still reviews and saves.
  const quickFill = async (text) => {
    if (!text.trim() || quickLoading) return;
    setQuickLoading(true);
    try {
      const { data } = await httpsCallable(functions, "parseEvent")({
        text,
        dateKey: localDateKey(new Date()),
        pods: Object.values(pods).map((p) => ({ id: p.id, name: p.name })),
      });
      setTitle(data.title);
      setStartDateValue(data.startDate);
      setEndDateValue(data.endDate);
      setStartTimeValue(data.startTime || "");
      setEndTimeValue(data.endTime || "");
      setEventKind(data.eventKind);
      setSelectedEventPods(data.podIds);
    } catch (err) {
      window.alert(err.code === "functions/resource-exhausted" ? "You've hit today's quick-add limit." : "Couldn't understand that. Try rephrasing, or fill in the form.");
    }
    setQuickLoading(false);
  };

  const addEvent = async () => {
    if (!title || !startDateValue) return;
    // memberEmails drives server-side read access. Always include the owner plus
    // every current member of each shared pod.
    const memberEmails = Array.from(new Set([
      currentUserEmail,
      ...selectedEventPods.flatMap((podId) => pods[podId]?.members || []),
    ]));
    const data = {
      title,
      startDate: startDateValue,
      endDate: endDateValue || startDateValue,
      startTime: startTimeValue || null,
      endTime: endTimeValue || null,
      notes: notes || null,
      location: location.trim() || null,
      ...(repeat && { repeat: cleanRepeat(repeat, startDateValue) }),
      sharedPods: selectedEventPods,
      memberEmails,
      ownerUid: currentUserUid,
      ownerEmail: currentUserEmail,
      eventKind,
      createdAt: Date.now(),
    };
    // Busy: pods get the placeholder; the real details go to eventPrivate in the same batch
    const isBusy = eventKind === "busy";
    const ref = doc(collection(db, "events"));
    const batch = writeBatch(db);
    batch.set(ref, isBusy ? { ...data, ...BUSY_PUBLIC } : data);
    if (isBusy) batch.set(doc(db, "eventPrivate", ref.id), { ownerUid: currentUserUid, title: data.title, location: data.location, notes: data.notes });
    await batch.commit();
    // Copies may still be loading (token refresh, tab focus): add it anyway rather than skip it silently
    if (syncToGoogle && googleAccessToken && googleCanWrite !== false) {
      // The event is saved either way; a Google failure only skips the copy.
      addGoogleCopy({ id: ref.id, ...data }).catch((err) => {
        console.error("Google Calendar sync failed:", err);
        window.alert("Event saved, but it couldn't be added to Google Calendar. You can turn on sync from the event.");
      });
    }
    // Notify each pod member (deduplicated) that a new event was shared with them.
    if (selectedEventPods.length > 0) {
      const recipients = new Map();
      for (const podId of selectedEventPods) {
        const pod = pods[podId];
        if (!pod) continue;
        for (const email of pod.members || []) {
          if (email !== currentUserEmail && !recipients.has(email)) {
            recipients.set(email, pod.name);
          }
        }
      }
      const notifTime = Date.now();
      await Promise.all(
        Array.from(recipients.entries()).map(([email, podName]) =>
          addDoc(collection(db, "notifications"), {
            type: "event_shared",
            actorEmail: currentUserEmail,
            recipientEmail: email,
            eventTitle: isBusy ? BUSY_PUBLIC.title : title,
            eventId: ref.id,
            ...rsvpNotifFields(eventKind, startDateValue, startTimeValue),
            podName,
            status: "unread",
            createdAt: notifTime,
          })
        )
      );
    }
    setTitle("");
    setStartTimeValue("");
    setEndTimeValue("");
    setNotes("");
    setLocation("");
    setSelectedEventPods([]);
    setEventKind("event");
    setRepeat(null);
    setShowAddEventModal(false);
  };

  // Close the Add Event form without saving (overlay tap, mobile swipe, ✕, Cancel). If anything was typed,
  // confirm first; closing then clears the form so the text doesn't reappear next time.
  const closeAddEvent = () => {
    if ((title.trim() || notes.trim() || location.trim()) && !window.confirm("Discard this event?")) return;
    setTitle("");
    setNotes("");
    setLocation("");
    setStartTimeValue("");
    setEndTimeValue("");
    setSelectedEventPods([]);
    setEventKind("event");
    setRepeat(null);
    setShowAddEventModal(false);
  };

  // Open Add Event with just this pod selected (Dashboard + Pods "Plan something")
  const planWithPod = (podId) => {
    setEventKind("event");
    setSelectedEventPods([podId]);
    setShowAddEventModal(true);
  };

  const createPod = async (name, membersToInvite) => {
    const toInvite = [...new Set((Array.isArray(membersToInvite) ? membersToInvite : []).map(normEmail))].filter(
      (e) => e && e !== currentUserEmail
    );
    // Creator is the only immediate member — everyone else must accept an invitation
    const ref = await addDoc(collection(db, "pods"), {
      name,
      members: [currentUserEmail],
      ownerUid: currentUserUid,
      ownerEmail: currentUserEmail,
      createdAt: Date.now(), // orders automatic pod colors (podColors.js)
    });
    // Schedules set to "Share to pods I join or create later" (pods you join: acceptInvitation does it)
    mySchedules.filter((sch) => sch.futurePods).forEach((sch) =>
      updateDoc(doc(db, "events", sch.id), { sharedPods: arrayUnion(ref.id) }).catch((err) => console.error("Schedule not added to the new pod", err)));
    for (const email of toInvite) {
      await addDoc(collection(db, "invitations"), {
        podId: ref.id,
        podName: name,
        inviterEmail: currentUserEmail,
        inviteeEmail: email,
        status: "pending",
        createdAt: Date.now(),
      });
    }
  };

  // A pod is "owned" by the current user if ownerUid matches, or if the pod
  // pre-dates the ownerUid field and has no owner recorded yet.
  const isPodOwner = (pod) =>
    pod && (pod.ownerUid === currentUserUid || (!pod.ownerUid && !pod.ownerEmail));

  const inviteMember = async (podId, rawEmail) => {
    const memberEmail = normEmail(rawEmail);
    if (!memberEmail) return;
    const pod = pods[podId];
    if (!pod || !isPodOwner(pod)) return;
    if ((pod.members || []).some((m) => normEmail(m) === memberEmail)) {
      alert("This person is already a member of this pod.");
      return;
    }
    const alreadyInvited = invitations.some(
      (inv) => inv.podId === podId && normEmail(inv.inviteeEmail) === memberEmail && inv.status === "pending"
    );
    if (alreadyInvited) {
      alert("A pending invitation already exists for this person.");
      return;
    }
    if (!window.confirm(`Send an invitation to ${memberEmail} to join "${pod.name}"?`)) return;
    await addDoc(collection(db, "invitations"), {
      podId,
      podName: pod.name,
      inviterEmail: currentUserEmail,
      inviteeEmail: memberEmail,
      status: "pending",
      createdAt: Date.now(),
    });
  };

  // Owner removes someone else — cannot remove themselves.
  const removeMemberFromPod = async (podId, memberEmail) => {
    const pod = pods[podId];
    if (!pod || !isPodOwner(pod)) return;
    if (memberEmail === currentUserEmail) return;
    if (!window.confirm(`Remove ${memberEmail} from "${pod.name}"? They will need a new invitation to rejoin.`)) return;
    // Server side, so their access to the pod's plans and notes is taken away too
    await httpsCallable(functions, "removePodMember")({ podId, email: memberEmail });
  };

  // Non-owner member leaves the pod (removes themselves only).
  const leavePod = async (podId) => {
    const pod = pods[podId];
    if (!pod || isPodOwner(pod)) return; // owners cannot leave — they must delete
    // Notify pod owner
    if (pod.ownerEmail) {
      await addDoc(collection(db, "notifications"), {
        type: "member_left",
        podId,
        podName: pod.name,
        actorEmail: currentUserEmail,
        recipientEmail: pod.ownerEmail,
        status: "unread",
        createdAt: Date.now(),
      });
    }
    await httpsCallable(functions, "leavePod")({ podId });
  };

  const renamePod = async (podId, newName) => {
    const pod = pods[podId];
    if (!pod || !isPodOwner(pod)) return;
    const name = newName.trim();
    if (!name || name === pod.name) return;
    await updateDoc(doc(db, "pods", podId), { name });
  };

  const deletePod = async (podId) => {
    const pod = pods[podId];
    if (!pod || !isPodOwner(pod)) return;
    if (!window.confirm(`Delete the pod "${pod.name}"? This cannot be undone.`)) return;
    await httpsCallable(functions, "deletePod")({ podId });
  };

  const createContact = async (firstName, lastName, email) => {
    const name = `${firstName} ${lastName}`.trim();
    await addDoc(collection(db, "contacts"), { firstName, lastName, name, email: normEmail(email), ownerUid: currentUserUid, ownerEmail: currentUserEmail });
  };

  const updateContact = async (id, firstName, lastName, email) => {
    const name = `${firstName} ${lastName}`.trim();
    await updateDoc(doc(db, "contacts", id), { firstName, lastName, name, email: normEmail(email) });
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await deleteDoc(doc(db, "contacts", id));
  };

  const isEventVisibleToUser = (ev) => {
    if (!ev) return false;
    // Hidden by this user (non-owner "remove from my calendar")
    if ((ev.hiddenBy || []).includes(currentUserEmail)) return false;
    // Google Calendar events are always the current user's own events
    if (ev._source === "google") return true;
    // Always visible to owner
    if (ev.ownerUid === currentUserUid || ev.ownerEmail === currentUserEmail) return true;
    return (ev.sharedPods || []).some((id) => id in pods);
  };

  const isEventOwner = (ev) =>
    ev && (ev.ownerUid === currentUserUid || ev.ownerEmail === currentUserEmail);

  // App events (own or shared) can be copied to Google — except the owner's twins of their own Google events.
  const canSyncToGoogle = (ev) =>
    !!ev && ev._source !== "google" && !(ev.googleEventId && isEventOwner(ev));

  const addGoogleCopy = async (ev) => {
    if (googleCopies?.[ev.id]) return;
    if (!appCalendarIdRef.current) appCalendarIdRef.current = await findAppCalendar(googleAccessToken, true);
    const calId = appCalendarIdRef.current;
    const body = toGoogleEvent(ev);
    const created = await gcal(googleAccessToken, `/calendars/${encodeURIComponent(calId)}/events`, { method: "POST", body });
    // Still loading (prev null): the load lists the whole calendar, so it will pick this copy up
    setGoogleCopies((prev) => prev && { ...prev, [ev.id]: { id: created.id, hash: body.extendedProperties.private.appHash } });
  };

  const removeGoogleCopy = async (appEventId) => {
    const copy = googleCopies?.[appEventId];
    if (!copy) return;
    try {
      await gcal(googleAccessToken, `/calendars/${encodeURIComponent(appCalendarIdRef.current)}/events/${encodeURIComponent(copy.id)}`, { method: "DELETE" });
    } catch (err) {
      if (err.status !== 404 && err.status !== 410) throw err; // already gone in Google
    }
    setGoogleCopies((prev) => {
      const next = { ...prev };
      delete next[appEventId];
      return next;
    });
  };

  // Keep copies in sync: update when the app event changes; delete when it's gone, hidden, or no longer shared with us.
  useEffect(() => {
    if (!googleCanWrite || !googleCopies || !dataReady || !appCalendarIdRef.current) return;
    const byId = new Map(allEventsFlat.map((ev) => [ev.id, ev]));
    Object.entries(googleCopies).forEach(([appEventId, copy]) => {
      if (syncingRef.current.has(appEventId)) return;
      const ev = byId.get(appEventId);
      const keep = canSyncToGoogle(ev) && isEventVisibleToUser(ev) && !ev.paused;
      const body = keep ? toGoogleEvent(ev) : null;
      if (keep && body.extendedProperties.private.appHash === copy.hash) return;
      syncingRef.current.add(appEventId);
      const path = `/calendars/${encodeURIComponent(appCalendarIdRef.current)}/events/${encodeURIComponent(copy.id)}`;
      (keep ? gcal(googleAccessToken, path, { method: "PATCH", body }) : gcal(googleAccessToken, path, { method: "DELETE" }))
        .then(() => true, (err) => {
          if (err.status === 404 || err.status === 410) return false; // deleted in Google — forget it
          console.error("Google copy sync failed:", err);
          return null;
        })
        .then((ok) => {
          if (ok === null) return;
          setGoogleCopies((prev) => {
            const next = { ...prev };
            if (keep && ok) next[appEventId] = { ...copy, hash: body.extendedProperties.private.appHash };
            else delete next[appEventId];
            return next;
          });
        })
        .finally(() => syncingRef.current.delete(appEventId));
    });
    // canSyncToGoogle/isEventVisibleToUser are render-closure helpers over pods + the current user, which are covered here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleCanWrite, googleCopies, dataReady, allEventsFlat, pods, googleAccessToken]);

  const toggleGoogleCopy = async (ev, on) => {
    try {
      if (on) await addGoogleCopy(ev);
      else await removeGoogleCopy(ev.id);
      // A schedule remembers it, so a paused-then-resumed schedule gets its copy back (effect below)
      if (ev.schedule && isEventOwner(ev)) await updateDoc(doc(db, "events", ev.id), { syncGoogle: on });
    } catch (err) {
      console.error("Google Calendar sync failed:", err);
      window.alert(`Couldn't ${on ? "add this event to" : "remove this event from"} Google Calendar. Please try again.`);
    }
  };

  // ── Schedules (Schedules.js): repeating Busy events shared with pods; the name is private like any Busy title ──
  const mySchedules = allEventsFlat.filter((ev) => ev.schedule && isEventOwner(ev))
    .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "") || (a.title || "").localeCompare(b.title || ""));

  const saveSchedule = async (v) => {
    const ex = scheduleDraft?.id ? scheduleDraft : null;
    const ref = ex ? doc(db, "events", ex.id) : doc(collection(db, "events"));
    const startDate = ex?.startDate || localDateKey(new Date());
    const now = Date.now();
    const data = {
      ...BUSY_PUBLIC,
      startDate,
      endDate: startDate,
      startTime: v.allDay ? null : v.startTime,
      endTime: v.allDay ? null : v.endTime,
      eventKind: "busy",
      repeat: cleanRepeat({ freq: "week", days: v.days, until: v.until }, startDate),
      schedule: true,
      futurePods: v.futurePods,
      syncGoogle: v.syncGoogle,
      sharedPods: v.podIds,
      memberEmails: Array.from(new Set([currentUserEmail, ...v.podIds.flatMap((id) => pods[id]?.members || [])])),
      updatedAt: now,
    };
    try {
      const batch = writeBatch(db);
      if (ex) batch.update(ref, data);
      else batch.set(ref, { ...data, paused: false, repeatSkip: [], ownerUid: currentUserUid, ownerEmail: currentUserEmail, createdAt: now });
      batch.set(doc(db, "eventPrivate", ref.id), { ownerUid: currentUserUid, title: v.name, location: null, notes: null });
      await batch.commit();
    } catch (err) {
      window.alert("Couldn't save this schedule: " + err.message);
      return;
    }
    setScheduleDraft(null);
    // Google copy: one repeating event in the FriSo calendar; later changes are patched by the sync effect
    if (googleAccessToken && googleCanWrite) {
      const has = !!googleCopies?.[ref.id];
      const job = v.syncGoogle && !has && !ex?.paused ? addGoogleCopy({ id: ref.id, ...data, title: v.name, paused: ex?.paused })
        : !v.syncGoogle && has ? removeGoogleCopy(ref.id) : null;
      job?.catch((err) => console.error("Google Calendar sync failed:", err));
    }
  };

  const toggleSchedulePause = (sch) =>
    updateDoc(doc(db, "events", sch.id), { paused: !sch.paused, updatedAt: Date.now() })
      .catch((err) => window.alert("Couldn't change this schedule: " + err.message));

  const deleteSchedule = async () => {
    const sch = scheduleDraft;
    if (!sch?.id || !window.confirm(`Delete the schedule "${sch.title || "Busy"}"? Your pods stop seeing these busy times.`)) return;
    try {
      await deleteDoc(doc(db, "events", sch.id));
      await deleteDoc(doc(db, "eventPrivate", sch.id)).catch(() => {});
    } catch (err) {
      window.alert("Couldn't delete this schedule: " + err.message);
      return;
    }
    setScheduleDraft(null); // its Google copy goes with the sync effect (the event is gone)
  };

  // Schedules with "Add to Google Calendar" get their copy back after being paused (pausing removes it)
  useEffect(() => {
    if (!googleCanWrite || !googleCopies || !dataReady || !appCalendarIdRef.current) return;
    mySchedules.filter((sch) => sch.syncGoogle && !sch.paused && !googleCopies[sch.id] && !syncingRef.current.has(sch.id)).forEach((sch) => {
      syncingRef.current.add(sch.id);
      addGoogleCopy(sch).catch((err) => console.error("Google Calendar sync failed:", err)).finally(() => syncingRef.current.delete(sch.id));
    });
    // mySchedules is derived from allEventsFlat each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleCanWrite, googleCopies, dataReady, allEventsFlat]);

  // Desktop month view: open the day popover beside the clicked cell, or close it if that day is already open.
  const toggleDayPreview = (key, cellEl) => {
    if (selectedDay === key && previewOpen) { setPreviewOpen(false); return; }
    previewAnchorRef.current = cellEl;
    setSelectedDay(key);
    setPreviewOpen(true);
  };

  // Place the popover right of its cell (left if it would overflow), clamped to the viewport. Runs every
  // render so it follows content height changes; before paint, so it never flashes in the wrong spot.
  useLayoutEffect(() => {
    const pop = previewRef.current;
    const cell = previewAnchorRef.current;
    if (!pop || !cell) return;
    const r = cell.getBoundingClientRect();
    const gap = 8;
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    const left = r.right + gap + w <= window.innerWidth - gap ? r.right + gap : Math.max(gap, r.left - gap - w);
    pop.style.left = `${left}px`;
    pop.style.top = `${Math.max(gap, Math.min(r.top, window.innerHeight - gap - h))}px`;
  });

  // Close the popover on outside click, Esc, page scroll or resize. Clicks on day cells are left to
  // toggleDayPreview so clicking another day moves the popover instead of closing it.
  useEffect(() => {
    if (!previewOpen) return;
    const close = () => setPreviewOpen(false);
    const onMouseDown = (e) => {
      if (previewRef.current?.contains(e.target) || e.target.closest?.(".calendar-cell")) return;
      close();
    };
    const onScroll = (e) => { if (!previewRef.current?.contains(e.target)) close(); };
    const onKeyDown = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [previewOpen]);

  // The anchor cell is gone once the month or view changes.
  useEffect(() => { setPreviewOpen(false); }, [calView, current]);

  // Desktop event popover: same placement rules as the day popover, using the rect saved at click time
  // (the anchor may be gone, e.g. a row in the day popover that closed).
  useLayoutEffect(() => {
    const pop = eventPopRef.current;
    const r = eventAnchorRef.current?.rect;
    if (isMobile || !pop) return;
    const gap = 8;
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    if (!r) { // opened from a list with no anchor on screen (e.g. Manage Google calendars): center it
      pop.style.left = `${Math.max(gap, (window.innerWidth - w) / 2)}px`;
      pop.style.top = `${Math.max(gap, (window.innerHeight - h) / 3)}px`;
      return;
    }
    const maxLeft = window.innerWidth - gap - w;
    const left = r.right + gap <= maxLeft ? r.right + gap
      : r.left - gap - w >= gap ? r.left - gap - w
      : Math.max(gap, Math.min(r.left, maxLeft));
    pop.style.left = `${left}px`;
    pop.style.top = `${Math.max(gap, Math.min(r.top, window.innerHeight - gap - h))}px`;
  });

  // Close the event popover on outside click, Esc, page scroll or resize. A mousedown on the chip that
  // opened it is ignored so its click can toggle it closed in openEvent. While editing, scroll/resize
  // leave it open and Esc only cancels the edit, so unsaved changes aren't lost by accident.
  const eventOpen = !!selectedEvent;
  useEffect(() => {
    if (!eventOpen || isMobile) return;
    const close = () => { if (!editing) closeEvent(); };
    const onMouseDown = (e) => {
      if (eventPopRef.current?.contains(e.target) || eventAnchorRef.current?.el?.contains(e.target)) return;
      closeEvent(); // outside click closes even while editing, as the old overlay click did
    };
    const onScroll = (e) => { if (!eventPopRef.current?.contains(e.target)) close(); };
    const onKeyDown = (e) => { if (e.key === "Escape") { if (editing) setEditing(false); else closeEvent(); } };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
    // closeEvent only calls state setters, so a stale copy is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventOpen, isMobile, editing]);

  // anchorEl: the clicked chip/row; on desktop the event opens as a popover beside it. Clicking the same
  // chip again closes it.
  const openEvent = (ev, anchorEl) => {
    if (anchorEl && anchorEl === eventAnchorRef.current?.el && selectedEvent?.id === ev.id) { closeEvent(); return; }
    eventAnchorRef.current = anchorEl ? { el: anchorEl, rect: anchorEl.getBoundingClientRect() } : null;
    setPreviewOpen(false);
    setEvMenuOpen(false);
    setSelectedEvent(ev);
    setEditing(false);
    setEditTitle(ev.title || "");
    setEditStartDateValue(ev.startDate || "");
    setEditEndDateValue(ev.endDate || ev.startDate || "");
    setEditStartTimeValue(ev.startTime || "");
    setEditEndTimeValue(ev.endTime || "");
    setEditNotes(ev.notes || "");
    setEditLocation(ev.location || "");
    setEditEventPods(ev.sharedPods || []);
    setEditEventKind(getEventKind(ev));
    setEditRepeat(ev.repeat || null);
    setRepeatScope(null);
    setGoogleShareKind("busy"); // sharing a Google event defaults to Busy (private)
    setGoogleSharePods([]);
    setShowGoogleShareForm(false);
  };

  // Busy events saved before details went private (2026-10-11) still carry their real title/notes/location in
  // the shared doc. Once everything has loaded, move them to eventPrivate (owner only; idempotent).
  useEffect(() => {
    if (!dataReady) return;
    const legacy = allEventsFlat.filter((ev) =>
      ev.ownerUid === currentUserUid && getEventKind(ev) === "busy" && !ev._private &&
      (ev.title !== BUSY_PUBLIC.title || ev.location || ev.notes || ev.htmlLink || ev.eventType));
    if (!legacy.length) return;
    const batch = writeBatch(db);
    legacy.slice(0, 200).forEach((ev) => {
      batch.set(doc(db, "eventPrivate", ev.id), { ownerUid: currentUserUid, title: ev.title || "", location: ev.location || null, notes: ev.notes || null, htmlLink: ev.htmlLink || null, eventType: ev.eventType || null }, { merge: true });
      batch.update(doc(db, "events", ev.id), BUSY_PUBLIC);
    });
    batch.commit().catch((err) => console.error("Moving Busy details to private failed", err));
  }, [dataReady, allEventsFlat, currentUserUid]);

  // Event ⋯ menu: close on a click outside it or Esc
  useEffect(() => {
    if (!evMenuOpen) return;
    const onDown = (e) => { if (!e.target.closest?.(".evc-head__actions .pg-menuwrap")) setEvMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); setEvMenuOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey, true); };
  }, [evMenuOpen]);

  const closeEvent = () => {
    setRepeatScope(null);
    setSelectedEvent(null);
    setEditing(false);
    setEvMenuOpen(false);
    setGoogleShareKind("busy");
    setGoogleSharePods([]);
    setShowGoogleShareForm(false);
  };

  const shareGoogleEvent = async () => {
    if (!selectedEvent || selectedEvent._source !== "google" || googleSharePods.length === 0) return;
    const memberEmails = Array.from(new Set([
      currentUserEmail,
      ...googleSharePods.flatMap((podId) => pods[podId]?.members || []),
    ]));
    const now = Date.now();
    const isBusy = googleShareKind === "busy";
    const details = { title: selectedEvent.title, notes: selectedEvent.notes ?? null, location: selectedEvent.location ?? null };
    const twinRef = doc(collection(db, "events"));
    const batch = writeBatch(db);
    if (isBusy) batch.set(doc(db, "eventPrivate", twinRef.id), { ownerUid: currentUserUid, ...details, htmlLink: selectedEvent.htmlLink || null, eventType: selectedEvent.eventType || null });
    batch.set(twinRef, {
      googleEventId: selectedEvent.id,
      ...(isBusy ? BUSY_PUBLIC : details),
      startDate: selectedEvent.startDate,
      endDate: selectedEvent.endDate,
      startTime: selectedEvent.startTime,
      endTime: selectedEvent.endTime,
      htmlLink: isBusy ? null : selectedEvent.htmlLink || null,
      eventType: isBusy ? null : selectedEvent.eventType || null,
      sharedPods: googleSharePods,
      memberEmails,
      ownerUid: currentUserUid,
      ownerEmail: currentUserEmail,
      eventKind: googleShareKind,
      createdAt: now,
      updatedAt: now,
    });
    await batch.commit();
    // Notify pod members
    const recipients = new Map();
    for (const podId of googleSharePods) {
      const pod = pods[podId];
      if (!pod) continue;
      for (const email of pod.members || []) {
        if (email !== currentUserEmail && !recipients.has(email)) {
          recipients.set(email, pod.name);
        }
      }
    }
    await Promise.all(
      Array.from(recipients.entries()).map(([email, podName]) =>
        addDoc(collection(db, "notifications"), {
          type: "event_shared",
          actorEmail: currentUserEmail,
          recipientEmail: email,
          eventTitle: isBusy ? BUSY_PUBLIC.title : selectedEvent.title,
          eventId: twinRef.id,
          ...rsvpNotifFields(googleShareKind, selectedEvent.startDate, selectedEvent.startTime),
          podName,
          status: "unread",
          createdAt: now,
        })
      )
    );
    closeEvent();
  };

  // The events/ doc behind what's open: for a repeat of a repeating event, its series doc (repeat.js)
  const seriesOf = (ev) => (ev?._seriesId ? allEventsFlat.find((e) => e.id === ev._seriesId) || { ...ev, id: ev._seriesId } : ev);
  const dayShift = (from, to) => Math.round((new Date(to + "T00:00") - new Date(from + "T00:00")) / 86400000);

  // scope (repeats only): "one" = just this date, "following" = this and the ones after, "all". Without a scope a
  // repeat asks first (repeatScope), unless repeating was just switched off — that keeps only this date.
  const saveEdit = async (scope) => {
    if (!selectedEvent || !selectedEvent.id) return;
    const series = seriesOf(selectedEvent);
    const occ = selectedEvent._occ;
    if (occ && editRepeat && !scope) { setRepeatScope("save"); return; }
    setRepeatScope(null);
    const id = series.id;
    // Recompute memberEmails based on the updated pod selection.
    const memberEmails = Array.from(new Set([
      selectedEvent.ownerEmail || currentUserEmail,
      ...editEventPods.flatMap((podId) => pods[podId]?.members || []),
    ]));
    const prevPods = selectedEvent.sharedPods || [];
    const addedPods = editEventPods.filter((pid) => !prevPods.includes(pid));
    const isBusy = editEventKind === "busy";
    const details = { title: editTitle, notes: editNotes || null, location: editLocation.trim() || null };
    // A shared Google event's link to the owner's Google Calendar follows the same rule as the details
    const link = selectedEvent.htmlLink || selectedEvent.eventType ? { htmlLink: selectedEvent.htmlLink || null, eventType: selectedEvent.eventType || null } : {};
    const now = Date.now();
    const nextRepeat = editRepeat ? cleanRepeat(editRepeat, editStartDateValue) : null;
    const fields = {
      ...(isBusy ? BUSY_PUBLIC : { ...details, ...link }),
      startDate: editStartDateValue,
      endDate: editEndDateValue || editStartDateValue,
      startTime: editStartTimeValue || null,
      endTime: editEndTimeValue || null,
      sharedPods: editEventPods,
      memberEmails,
      eventKind: editEventKind,
      updatedAt: now,
    };
    let savedId = id; // the doc the edit landed in (a new one for "one" / "following")
    let newDoc = null;
    try {
      // Busy: the event gets the placeholder and eventPrivate the real details; leaving Busy moves them back
      const batch = writeBatch(db);
      if (!occ || scope === "all" || (scope === "following" && occ === series.startDate) || !nextRepeat) {
        const f = { ...fields, repeat: nextRepeat };
        if (occ && nextRepeat) {
          // Whole series: moving this date moves every date by the same amount
          f.startDate = addDaysKey(series.startDate, dayShift(occ, editStartDateValue));
          f.endDate = addDaysKey(f.startDate, dayShift(fields.startDate, fields.endDate));
          f.repeat = cleanRepeat(editRepeat, f.startDate);
        }
        if (occ && !nextRepeat) f.repeatSkip = [];
        batch.update(doc(db, "events", id), f);
        if (isBusy) batch.set(doc(db, "eventPrivate", id), { ownerUid: currentUserUid, ...details, ...link });
        else if (selectedEvent._private) batch.delete(doc(db, "eventPrivate", id));
      } else {
        batch.update(doc(db, "events", id), scope === "one"
          ? { repeatSkip: arrayUnion(occ), updatedAt: now }
          : { repeat: { ...series.repeat, until: addDaysKey(occ, -1) }, updatedAt: now });
        const ref = doc(collection(db, "events"));
        savedId = ref.id;
        newDoc = { ...fields, ...(scope === "following" && { repeat: nextRepeat }), ownerUid: series.ownerUid || currentUserUid, ownerEmail: series.ownerEmail || currentUserEmail, createdAt: now };
        batch.set(ref, newDoc);
        if (isBusy) batch.set(doc(db, "eventPrivate", ref.id), { ownerUid: currentUserUid, ...details });
      }
      await batch.commit();
      // A split-off part of a series that's in Google gets its own copy
      if (newDoc && googleCopies?.[id] && googleCanWrite) {
        addGoogleCopy({ id: savedId, ...newDoc, ...(isBusy && details) }).catch((err) => console.error("Google Calendar sync failed:", err));
      }
      // Notify members of newly added pods ("New event"); everyone else it's shared with hears what changed
      const recipients = new Map();
      if (addedPods.length > 0) {
        for (const podId of addedPods) {
          const pod = pods[podId];
          if (!pod) continue;
          for (const email of pod.members || []) {
            if (email !== currentUserEmail && !recipients.has(email)) {
              recipients.set(email, pod.name);
            }
          }
        }
        await Promise.all(
          Array.from(recipients.entries()).map(([email, podName]) =>
            addDoc(collection(db, "notifications"), {
              type: "event_shared",
              actorEmail: currentUserEmail,
              recipientEmail: email,
              eventTitle: isBusy ? BUSY_PUBLIC.title : editTitle,
              eventId: savedId,
              ...rsvpNotifFields(editEventKind, editStartDateValue, editStartTimeValue),
              podName,
              status: "unread",
              createdAt: now,
            })
          )
        );
      }
      const changed = changeNotifs({
        before: selectedEvent,
        after: fields,
        eventId: savedId, actorEmail: currentUserEmail, skip: [...recipients.keys()], now,
      });
      await Promise.all(changed.map((n) => addDoc(collection(db, "notifications"), n))).catch(() => {}); // the edit is saved either way
    } catch (err) {
      console.error("saveEdit failed", err);
      window.alert("Couldn't save this event: " + err.message);
      return;
    }
    setEditing(false);
    setSelectedEvent(null);
  };

  // Owner deletes (a repeat asks which ones, like saveEdit); anyone else hides it — all repeats — from their calendar.
  const deleteEvent = async (scope) => {
    if (!selectedEvent || !selectedEvent.id) return;
    const series = seriesOf(selectedEvent);
    const eventId = series.id;
    const occ = selectedEvent._occ;
    const ref = doc(db, "events", eventId);
    const isMine = (e) => (e._seriesId || e.id) === eventId;
    // Everyone it was shared with hears it's cancelled (planChanges.js); the delete stands either way
    const notifyCancelled = (following) => Promise.all(
      cancelNotifs({ ev: selectedEvent, date: occ || selectedEvent.startDate, following, actorEmail: currentUserEmail })
        .map((n) => addDoc(collection(db, "notifications"), n))
    ).catch(() => {});
    try {
      if (isEventOwner(selectedEvent)) {
        if (occ && !scope) { setRepeatScope("delete"); return; }
        setRepeatScope(null);
        if (occ && (scope === "one" || (scope === "following" && occ > series.startDate))) {
          await updateDoc(ref, scope === "one"
            ? { repeatSkip: arrayUnion(occ), updatedAt: Date.now() }
            : { repeat: { ...series.repeat, until: addDaysKey(occ, -1) }, updatedAt: Date.now() });
          notifyCancelled(scope === "following");
          closeEvent();
          return;
        }
        if (!occ && !window.confirm("Delete this event for everyone in the pod?")) return;
        await deleteDoc(ref);
        notifyCancelled(false);
        if (selectedEvent._private) await deleteDoc(doc(db, "eventPrivate", eventId)).catch(() => {});
        // Optimistically remove from all local state; snapshots will confirm.
        setAllEventsFlat((prev) => prev.filter((e) => e.id !== eventId));
        setDisplayEvents((prev) => prev.filter((e) => !isMine(e)));
        setEvents((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => {
            next[k] = next[k].filter((e) => !isMine(e));
            if (!next[k].length) delete next[k];
          });
          return next;
        });
      } else {
        if (!window.confirm(occ ? "Remove this repeating event from your calendar (every date)? It will stay on the pod." : "Remove this event from your calendar? It will stay on the pod.")) return;
        await updateDoc(ref, { hiddenBy: arrayUnion(currentUserEmail) });
        // Optimistically mark hidden in local state so it disappears immediately.
        const markHidden = (ev) => (isMine(ev) ? { ...ev, hiddenBy: [...(ev.hiddenBy || []), currentUserEmail] } : ev);
        setAllEventsFlat((prev) => prev.map(markHidden));
        setDisplayEvents((prev) => prev.map(markHidden));
        setEvents((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => { next[k] = next[k].map(markHidden); });
          return next;
        });
      }
      closeEvent();
    } catch (err) {
      alert("Could not delete event: " + err.message);
    }
  };

  // Real contacts the user owns
  const visibleContacts = Object.values(contacts).filter((c) =>
    c.ownerUid === currentUserUid ||
    (!c.ownerUid && c.ownerEmail === currentUserEmail)
  );

  // Pod members who don't already have a contact entry — shown as read-only
  const ownedEmails = new Set(visibleContacts.map((c) => c.email));
  ownedEmails.add(currentUserEmail); // never add yourself
  const podMemberEntries = [];
  const seenPodEmails = new Set(ownedEmails);
  Object.values(pods).forEach((pod) => {
    (pod.members || []).forEach((email) => {
      if (!seenPodEmails.has(email)) {
        seenPodEmails.add(email);
        podMemberEntries.push({
          id: `__pod__${email}`,
          email,
          name: email,
          firstName: "",
          lastName: "",
          _podName: pod.name,
        });
      }
    });
  });

  // All contacts shown in the UI = owned contacts + pod-derived entries
  const allVisibleContacts = [...visibleContacts, ...podMemberEntries];

  // Merge Google events into the per-day map and flat list (allEventsFlat = owned + memberEmails).
  // Suppress raw Google events that have a Firestore twin (shared via this app).
  // The Firestore twin shows in allEventsFlat instead, keeping sync as the source of truth.
  const sharedGoogleIds = new Set(
    allEventsFlat
      .filter((ev) => ev.googleEventId && (ev.ownerUid === currentUserUid || ev.ownerEmail === currentUserEmail))
      .map((ev) => ev.googleEventId)
  );
  const unsharedGoogleEvents = googleEvents.filter((ev) => !sharedGoogleIds.has(ev.id));

  const mergedEvents = { ...events };
  unsharedGoogleEvents.forEach((ev) => {
    const start = ev.startDate;
    const end = ev.endDate || start;
    if (!start) return;
    let cur = new Date(start + "T00:00");
    const endD = new Date(end + "T00:00");
    while (cur <= endD) {
      const key = localDateKey(cur);
      if (!mergedEvents[key]) mergedEvents[key] = [];
      mergedEvents[key] = [...mergedEvents[key], ev];
      cur.setDate(cur.getDate() + 1);
    }
  });
  const allEventsForDisplay = [...displayEvents, ...unsharedGoogleEvents];


  // Month view scrolls continuously: one block per month (heading + its weeks; days of the neighbouring
  // months are blank), from monthRange.from to monthRange.to months from today's month. Extended by 6
  // months when the scroll nears either end.
  const thisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const rangeFrom = new Date(thisMonth.getFullYear(), thisMonth.getMonth() + monthRange.from, 1);
  const months = Array.from({ length: monthRange.to - monthRange.from + 1 }, (_, i) => {
    const first = new Date(rangeFrom.getFullYear(), rangeFrom.getMonth() + i, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    return { key: localDateKey(first).slice(0, 7), first, startKey: localDateKey(first), endKey: localDateKey(last), weeks: weeksBetween(first, last) };
  });
  const monthLabel = current.toLocaleString(undefined, { month: "long", year: "numeric" });
  const hours = Array.from({ length: GRID_END - GRID_START }, (_, i) => GRID_START + i);
  const weekDays = getWeekDays(current);
  // On mobile, week view collapses to day view automatically
  const effectiveCalView = isMobile && calView === "week" ? "day" : calView;

  // Scrolling month view: jump to a month/week when asked, and keep the view still when months are prepended
  useLayoutEffect(() => {
    const sc = monthScrollRef.current;
    if (!sc) return;
    if (monthPrevHeight.current !== null) {
      sc.scrollTop += sc.scrollHeight - monthPrevHeight.current;
      monthPrevHeight.current = null;
      return;
    }
    const target = monthScrollTo.current;
    if (!target) return;
    monthScrollTo.current = null;
    // "month": the top of current's month block; a date key: that week's row inside its month's block
    const key = target === "month" ? null : target;
    const block = sc.querySelector(`[data-month="${(key || localDateKey(current)).slice(0, 7)}"]`);
    const row = key ? [...(block?.querySelectorAll("[data-week]") || [])].find((el) => el.dataset.week <= key && key <= el.dataset.weekEnd) : null;
    const el = row || block;
    if (el) sc.scrollTop = el.offsetTop - sc.offsetTop - (row ? 36 : 0); // a little of the month heading stays in view
  });

  // Desktop: the pane runs to the bottom of the window, and week rows are sized so a whole month (heading +
  // up to 6 weeks) fits it; cells then show as many event chips as fit and "+N". Phones keep their fixed dot rows.
  useEffect(() => {
    const sc = monthScrollRef.current;
    if (!sc || isMobile || view !== "calendar" || effectiveCalView !== "month") { setMonthRowH(0); return; }
    const measure = () => {
      const paneH = Math.max(480, Math.floor(window.innerHeight - sc.getBoundingClientRect().top - 28)); // 28: panel padding + a margin
      sc.style.height = `${paneH}px`;
      // pane − month heading (32) − 6 row gaps (4) − block padding (8), over 6 rows; under 84px a day gets cramped, so a short window scrolls instead
      setMonthRowH(Math.max(84, Math.floor((paneH - 32 - 6 * 4 - 8) / 6)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("resize", measure); sc.style.height = ""; };
  }, [isMobile, view, effectiveCalView]);

  const onMonthScroll = () => {
    const sc = monthScrollRef.current;
    if (!sc) return;
    // The month in view = the month block crossing a third of the way down
    const probe = sc.scrollTop + sc.clientHeight / 3;
    const block = [...sc.querySelectorAll("[data-month]")].find((el) => el.offsetTop - sc.offsetTop + el.offsetHeight > probe);
    if (block) {
      const [y, m] = block.dataset.month.split("-").map(Number);
      if (y !== current.getFullYear() || m - 1 !== current.getMonth()) setCurrent(new Date(y, m - 1, 1));
    }
    if (monthPrevHeight.current !== null) return; // a prepend is already pending
    if (sc.scrollTop < 300) {
      monthPrevHeight.current = sc.scrollHeight;
      setMonthRange((r) => ({ ...r, from: r.from - 6 }));
    } else if (sc.scrollHeight - sc.scrollTop - sc.clientHeight < 300) {
      setMonthRange((r) => ({ ...r, to: r.to + 6 }));
    }
  };
  const goToday = () => {
    const now = new Date();
    setCurrent(now);
    setSelectedDay(localDateKey(now));
    monthScrollTo.current = "month"; // the whole month fits, so show it from its heading (layout effect, after this render)
  };

  // Every visit to the Calendar tab opens on today. The month scroller is re-created on each visit and would
  // otherwise sit at its top (6 months back); this runs with the row-height measure, so the jump uses final sizes.
  useEffect(() => {
    if (view === "calendar") goToday();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from a notification (App's eventIntent, after the switch to this tab): once events have loaded, show
  // the event's week and open its card. Declared after the effect above so it wins over "open on today".
  useEffect(() => {
    if (!eventIntent || !dataReady) return;
    onIntentDone();
    const series = allEventsFlat.find((e) => e.id === eventIntent.eventId);
    // A repeating event opens on its next date (else its last one)
    const reps = series?.repeat ? displayEvents.filter((e) => e._seriesId === series.id) : [];
    const ev = series?.repeat ? reps.find((e) => e.startDate >= localDateKey(new Date())) || reps[reps.length - 1] : series;
    if (!ev || !isEventVisibleToUser(ev)) { window.alert("This event isn't available anymore."); return; }
    const d = new Date(ev.startDate + "T00:00"), now = new Date();
    const diff = (d.getFullYear() - now.getFullYear()) * 12 + d.getMonth() - now.getMonth();
    setMonthRange((r) => ({ from: Math.min(r.from, diff - 1), to: Math.max(r.to, diff + 1) }));
    setCurrent(d);
    setSelectedDay(ev.startDate);
    monthScrollTo.current = ev.startDate;
    openEvent(ev, null);
  }, [eventIntent, dataReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const navLabel = (() => {
    if (effectiveCalView === "month") return monthLabel;
    if (effectiveCalView === "week") {
      const wds = getWeekDays(current);
      const s = wds[0], e = wds[6];
      if (s.getMonth() === e.getMonth()) {
        return `${s.toLocaleString(undefined, { month: "short" })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
      }
      return `${s.toLocaleString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleString(undefined, { month: "short", day: "numeric" })}, ${e.getFullYear()}`;
    }
    return current.toLocaleString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" });
  })();

  // Calendar filter chips (resets each visit). Only what the calendar draws — Google sync, the Dashboard
  // and Pods keep using isEventVisibleToUser. An event shows if any of its pods (or "me"/"google") is on.
  const calKeys = (ev) => {
    if (ev._source === "google") return [`g:${ev._calendarId}`];
    const ids = (ev.sharedPods || []).filter((id) => id in pods);
    return ids.length ? ids : ["me"];
  };
  // Schedules (repeating Busy, Schedules.js) stay off the calendar unless the Schedules chip is on: five people's work
  // hours would bury everything else. Yours show as a light band in week/day view; everyone's still count in Find a time.
  const calVisible = (ev) => isEventVisibleToUser(ev) && (!ev.schedule || showSchedules) && calKeys(ev).some((k) => !calHidden.includes(k));
  // Google calendars: is one shown, and which of your shared copies came from it
  const primaryCalId = googleCalendars.find((c) => c.primary)?.id;
  const shownCalIds = pickedCalendarIds(shownCalendars, googleCalendars);
  const isCalShown = (calId) => shownCalIds.includes(calId);
  const toggleShownCalendar = (calId) => {
    const next = isCalShown(calId) ? shownCalIds.filter((id) => id !== calId) : [...shownCalIds, calId];
    setShownCalendars(next);
    setDoc(doc(db, "userSettings", currentUserUid), { googleCalendars: next }, { merge: true })
      .catch((err) => window.alert("Couldn't save your calendar choice: " + err.message));
  };
  // "You're seeing N of M Google calendars" banner: only on the first Calendar visit where some calendars are
  // hidden, never again after that (calendarBannerSeen is saved as soon as it shows)
  const hiddenCals = googleCalendars.filter((c) => !isCalShown(c.id));
  const markCalBannerSeen = () => {
    setCalBannerSeen(true);
    setDoc(doc(db, "userSettings", currentUserUid), { calendarBannerSeen: true }, { merge: true }).catch(() => {});
  };
  useEffect(() => {
    if (view !== "calendar") { setCalBannerNow(false); return; }
    if (calBannerSeen !== false || hiddenCals.length === 0) return;
    setCalBannerNow(true);
    markCalBannerSeen();
  }, [view, calBannerSeen, hiddenCals.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const showHiddenCalsBanner = calBannerNow && !calPickerOpen && hiddenCals.length > 0;
  const closeCalPicker = () => { setCalPickerOpen(false); setCalPickerExpanded(null); setCalBannerNow(false); };
  // The one-time welcome (Welcome.js): accounts made from 2026-10-12 on that haven't finished or skipped it.
  // "?welcome" in the URL shows it anyway (for checking it); "?welcome=2" / "=3" opens on that step and never skips
  // the pod step, so it can be checked on an account that's already in a pod.
  const welcomeStep = Number(new URLSearchParams(window.location.search).get("welcome")) || 0;
  const showWelcome = !welcomeDone && (new URLSearchParams(window.location.search).has("welcome") ||
    (onboardedAt === null && dataReady && new Date(auth.currentUser?.metadata?.creationTime || 0) >= new Date("2026-10-12T00:00:00Z")));
  const finishWelcome = () => {
    setWelcomeDone(true);
    // Drop "?welcome" too: Notes unmounts this component (welcomeDone resets), and the param would show it again
    const url = new URL(window.location.href);
    if (url.searchParams.has("welcome")) { url.searchParams.delete("welcome"); window.history.replaceState(null, "", url); }
    setDoc(doc(db, "userSettings", currentUserUid), { onboardedAt: Date.now() }, { merge: true }).catch(() => {});
  };
  const listNames = (names) => names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  const twinCalendarId = (ev) => { const k = calendarOfGoogleKey(ev.googleEventId); return k === "primary" ? primaryCalId : k; };
  const sharedFromCalendar = (calId) => allEventsFlat
    .filter((ev) => ev.googleEventId && ev.ownerUid === currentUserUid && twinCalendarId(ev) === calId)
    .sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const toggleCalFilter = (k) => setCalHidden((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  // Colors for an event: dot (solid), bg + text (tinted). Pod color wins for events; kinds override.
  const evColors = (ev) => {
    const kind = getEventKind(ev);
    if (ev._source === "google") return { dot: ev._calendarColor || "var(--google-accent)", bg: "var(--google-bg)", text: "var(--google-text)" }; // dot: the calendar's own Google color
    if (kind === "busy") return { dot: "var(--busy-accent)", bg: "var(--busy-bg)", text: "var(--busy-text)" };
    if (kind === "traveling") return { dot: "var(--travel-accent)", bg: "var(--travel-bg)", text: "var(--travel-text)" };
    const podId = (ev.sharedPods || []).find((id) => id in pods);
    if (podId) { const c = getPodColor(podId); return { dot: c.border, bg: c.bg, text: c.text }; }
    return { dot: "var(--color-primary)", bg: "var(--color-primary-light)", text: "var(--color-text)" };
  };
  // "19:30" → "7:30p" for month chips
  const shortTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    return `${((h + 11) % 12) + 1}${m ? ":" + String(m).padStart(2, "0") : ""}${h >= 12 ? "p" : "a"}`;
  };
  const sortByTime = (a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00");
  const dayEventsFor = (key) => [...new Map((mergedEvents[key] || []).filter(calVisible).map((ev) => [ev.id, ev])).values()].sort(sortByTime);
  const schedulesOn = (key) => (showSchedules ? [] : (mergedEvents[key] || []).filter((ev) => ev.schedule && isEventVisibleToUser(ev))).sort(sortByTime);
  const anySchedules = allEventsFlat.some((ev) => ev.schedule && !ev.paused && isEventVisibleToUser(ev));
  const { nameOf, initialsOf } = makeNamer(contacts, currentUserEmail);
  // Under a day's plans while the Schedules chip is off: "You: Work 9a–5p" and "Busy: Maya 9a–5p · Sam 8:30a–5:30p"
  const renderScheduleLines = (key) => {
    const list = schedulesOn(key);
    if (!list.length) return null;
    const when = (ev) => (ev.startTime ? `${shortTime(ev.startTime)}${ev.endTime ? `–${shortTime(ev.endTime)}` : ""}` : "all day");
    const mine = list.filter((ev) => isEventOwner(ev));
    const others = list.filter((ev) => !isEventOwner(ev));
    return (
      <div className="cal-sched-lines">
        <div className="dash-eyebrow">Schedules</div>
        {mine.length > 0 && <div><b>You</b> {mine.map((ev) => `${ev.title} ${when(ev)}`).join(", ")}</div>}
        {others.length > 0 && <div><b>Busy</b> {others.map((ev) => `${nameOf(ev.ownerEmail).split(" ")[0]} ${when(ev)}`).join(" · ")}</div>}
      </div>
    );
  };
  // One row in the desktop day popover and the phone day list
  const renderDayRow = (ev, onOpen) => {
    const kind = getEventKind(ev);
    const multi = ev.endDate && ev.endDate !== ev.startDate;
    const fmtDate = (d) => new Date(d + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const podNames = (ev.sharedPods || []).map((id) => pods[id]?.name).filter(Boolean);
    const sub = [
      ev._source === "google" ? "Google Calendar" : kind === "busy" ? "Busy" : kind === "traveling" ? "Away" : podNames.length ? podNames.join(", ") : "Just you",
      multi ? `${fmtDate(ev.startDate)} – ${fmtDate(ev.endDate)}` : null,
      ev.location || null,
    ].filter(Boolean).join(" · ");
    return (
      <button key={ev.id} type="button" className="cal-row" onClick={(e) => onOpen(ev, e.currentTarget)}>
        <span className="cal-row__time">{ev.startTime ? <>{formatTime(ev.startTime)}{ev.endTime && <small>{formatTime(ev.endTime)}</small>}</> : "All day"}</span>
        <i style={{ background: evColors(ev).dot }} />
        <span className="cal-row__text">{ev.title}<small>{sub}</small></span>
      </button>
    );
  };

  const prevPeriod = () => {
    const d = new Date(current);
    d.setDate(d.getDate() - (effectiveCalView === "week" ? 7 : 1));
    setCurrent(d);
  };
  const nextPeriod = () => {
    const d = new Date(current);
    d.setDate(d.getDate() + (effectiveCalView === "week" ? 7 : 1));
    setCurrent(d);
  };

  const renderTimeGrid = (cols) => {
    const gridStyle = { gridTemplateColumns: `48px repeat(${cols.length}, 1fr)` };
    return (
      <div className="time-grid">
        {/* Column headers */}
        <div className="time-grid__header" style={gridStyle}>
          <div className="time-grid__gutter-header" />
          {cols.map(({ day, key }) => {
            const isToday = key === todayKey;
            return (
              <div key={key} className={`time-grid__col-header${isToday ? " time-grid__col-header--today" : ""}`}>
                <div className="time-grid__col-hday">{day.toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div className={`time-grid__col-hdate${isToday ? " time-grid__col-hdate--today" : ""}`}>{day.getDate()}</div>
              </div>
            );
          })}
        </div>
        {/* All-day strip */}
        {(() => {
          const hasAllDay = cols.some(({ key }) =>
            (mergedEvents[key] || []).filter(calVisible).some((ev) => !ev.startTime)
          );
          if (!hasAllDay) return null;
          return (
            <div className="time-grid__allday-row" style={gridStyle}>
              <div className="time-grid__gutter-label" style={{ fontSize: 10, color: "var(--color-text-muted)", textAlign: "right", paddingRight: 6, paddingTop: 4 }}>all-day</div>
              {cols.map(({ key }) => {
                const allDayEvs = (mergedEvents[key] || []).filter(calVisible).filter((ev) => !ev.startTime);
                return (
                  <div key={key} className="time-grid__allday-cell">
                    {allDayEvs.map((ev) => {
                      const col = evColors(ev);
                      return (
                        <button key={ev.id} type="button" className="cal-event cal-event--bar" style={{ background: col.bg, color: col.text }}
                          onClick={(e) => openEvent(ev, e.currentTarget)}>
                          <span className="cal-event__title">{getEventKind(ev) === "traveling" ? "Away · " : ""}{ev.title}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}
        {/* Scrollable time body */}
        <div className="time-grid__scroll">
          <div className="time-grid__body" style={gridStyle}>
            <div className="time-grid__gutter">
              {hours.map((h) => (
                <div key={h} className="time-grid__hour-label" style={{ height: SLOT_H }}>{formatHour(h)}</div>
              ))}
            </div>
            {cols.map(({ day, key }) => {
              const isToday = key === todayKey;
              const timedEvs = (mergedEvents[key] || []).filter(calVisible).filter((ev) => !!ev.startTime);
              return (
                <div key={key} className={`time-grid__day-col${isToday ? " time-grid__day-col--today" : ""}`} style={{ height: hours.length * SLOT_H }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const totalMins = GRID_START * 60 + (y / SLOT_H) * 60;
                    const h = Math.floor(totalMins / 60);
                    const m = Math.round((totalMins % 60) / 30) * 30;
                    const safeM = m >= 60 ? 30 : m;
                    const startStr = `${String(h).padStart(2, "0")}:${String(safeM).padStart(2, "0")}`;
                    const endH = h + 1 > 23 ? 23 : h + 1;
                    const endStr = `${String(endH).padStart(2, "0")}:${String(safeM).padStart(2, "0")}`;
                    setContextMenu({ x: e.clientX, y: e.clientY, dateKey: key, startTime: startStr, endTime: endStr });
                  }}
                  onDoubleClick={(e) => {
                    if (e.target.closest(".time-grid__event")) return; // double-click on an event, not empty time
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const totalMins = GRID_START * 60 + (y / SLOT_H) * 60;
                    const h = Math.floor(totalMins / 60);
                    const m = Math.round((totalMins % 60) / 30) * 30;
                    const safeM = m >= 60 ? 30 : m;
                    const startStr = `${String(h).padStart(2, "0")}:${String(safeM).padStart(2, "0")}`;
                    const endH = h + 1 > 23 ? 23 : h + 1;
                    const endStr = `${String(endH).padStart(2, "0")}:${String(safeM).padStart(2, "0")}`;
                    setStartDateValue(key);
                    setEndDateValue(key);
                    setStartTimeValue(startStr);
                    setEndTimeValue(endStr);
                    setShowAddEventModal(true);
                  }}
                >
                  {hours.map((h) => <div key={h} className="time-grid__hour-cell" style={{ height: SLOT_H }} />)}
                  {/* Your schedules (Schedules chip off): a light band behind your plans, not blocks on top */}
                  {schedulesOn(key).filter((ev) => ev.startTime && isEventOwner(ev)).map((ev) => {
                    const top = eventTop(ev.startTime);
                    if (top === null) return null;
                    return <div key={`band-${ev.id}`} className="time-grid__band" style={{ top, height: eventHeight(ev.startTime, ev.endTime) }} aria-hidden="true">{ev.title}</div>;
                  })}
                  {timedEvs.map((ev) => {
                    const t = ev.startTime;
                    const top = eventTop(t);
                    if (top === null || top < 0 || top > hours.length * SLOT_H) return null;
                    const h = eventHeight(t, ev.endTime);
                    const kind = getEventKind(ev);
                    const col = evColors(ev);
                    return (
                      <div key={ev.id} className={`time-grid__event${kind === "busy" ? " time-grid__event--busy" : ""}`}
                        style={{ top, height: h, background: col.bg, color: col.text }}
                        onClick={(e) => openEvent(ev, e.currentTarget)}>
                        <div className="time-grid__event-title">{kind === "traveling" ? "Away · " : ""}{ev.title}</div>
                        {h > 28 && <div className="time-grid__event-time">{formatTime(t)}{ev.endTime ? ` – ${formatTime(ev.endTime)}` : ""}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {showWelcome && (
        <Welcome
          firstName={(auth.currentUser?.displayName || "").split(" ")[0]}
          hasPod={!welcomeStep && Object.keys(pods).length > 0}
          startStep={welcomeStep >= 1 && welcomeStep <= 3 ? welcomeStep : 1}
          pendingInvitations={pendingInvitations || []}
          onAcceptInvitation={onAcceptInvitation}
          onDeclineInvitation={onDeclineInvitation}
          onCreatePod={createPod}
          googleConnected={googleConnected || !!googleAccessToken}
          googleCalendars={googleCalendars}
          isCalShown={isCalShown}
          onToggleCalendar={toggleShownCalendar}
          onConnectGoogle={onReconnectGoogle}
          onDone={() => { finishWelcome(); markCalBannerSeen(); }}
        />
      )}
      {/* ── Calendar view ── */}
      {view === "calendar" && (
        <>
          {/* Header: title, ‹ › and Today, view switch, New event */}
          <div className="cal-head">
            <div className="cal-head__left">
              <h1 className="cal-title">
                {effectiveCalView === "month"
                  ? <>{current.toLocaleString(undefined, { month: "long" })} <span>{current.getFullYear()}</span></>
                  : navLabel}
              </h1>
              <div className="cal-head__nav">
                {effectiveCalView !== "month" && <>
                <button type="button" className="cal-navbtn" aria-label="Previous" onClick={prevPeriod}><svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg></button>
                <button type="button" className="cal-navbtn" aria-label="Next" onClick={nextPeriod}><svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg></button>
                </>}
                <button type="button" className={`btn btn--secondary btn--sm cal-today${effectiveCalView === "month" ? " cal-today--month" : ""}`} onClick={goToday}>Today</button>
              </div>
            </div>
            <div className="cal-head__right">
              <div className="cal-view-toggle" role="group" aria-label="View">
                {(isMobile ? ["month", "day"] : ["month", "week", "day"]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={calView === v}
                    className={`cal-view-toggle__btn${calView === v ? " cal-view-toggle__btn--active" : ""}`}
                    onClick={() => { if (v === "month") monthScrollTo.current = "month"; setCalView(v); }}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
              <button type="button" className="btn btn--primary pg-btn cal-new" aria-label="New event" onClick={() => setShowAddEventModal(true)}>
                <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg><span>New event</span>
              </button>
            </div>
          </div>

          {/* No lasting Google connection yet (googleConnected === false), for someone who uses Google: either the
              hour-long token from "Continue with Google" is all they have, or nothing at all (e.g. after a password
              sign-in). Connecting once keeps it connected (the refresh token lives server-side). */}
          {googleConnected === false && (googleAccessToken || auth.currentUser?.providerData?.some((pd) => pd.providerId === "google.com")) && (
            <div className="cal-reconnect" role="status">
              <span>{googleAccessToken
                ? "Connect Google Calendar once to keep it connected. Otherwise your Google events stop showing after an hour."
                : "Your Google Calendar isn't connected, so its events aren't showing."}</span>
              <button type="button" className="btn btn--secondary btn--sm pg-btn" disabled={googleConnecting} onClick={onReconnectGoogle}>{googleConnecting ? "Connecting…" : "Connect Google Calendar"}</button>
            </div>
          )}

          {/* Some Google calendars are switched off: say so once (until dismissed or the picker is opened) */}
          {showHiddenCalsBanner && (
            <div className="cal-banner" role="status">
              <span className="cal-banner__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg></span>
              <span className="cal-banner__text">
                <b>You're seeing {googleCalendars.length - hiddenCals.length} of {googleCalendars.length} Google calendars.</b>{" "}
                {listNames(hiddenCals.map((c) => c.name))} {hiddenCals.length === 1 ? "is" : "are"} turned off, so {hiddenCals.length === 1 ? "its" : "their"} events don't show here.
              </span>
              <button type="button" className="btn btn--primary btn--sm pg-btn" onClick={() => setCalPickerOpen(true)}>Choose calendars</button>
              <button type="button" className="cal-banner__close" aria-label="Dismiss" onClick={() => setCalBannerNow(false)}>×</button>
            </div>
          )}

          {/* Filter chips: show or hide a pod's plans (and your own / Google) */}
          {(Object.keys(pods).length > 0 || googleCalendars.length > 0 || anySchedules) && (
            <div className="cal-filters" role="group" aria-label="Show">
              {[
                ...Object.values(pods).sort((x, y) => x.name.localeCompare(y.name)).map((p) => [p.id, p.name, getPodColor(p.id).border]),
                ["me", "Just me", "var(--color-primary)"],
                // One chip per Google calendar being shown
                ...googleCalendars
                  .filter((c) => isCalShown(c.id))
                  .map((c) => [`g:${c.id}`, c.primary ? "Google" : c.name, c.color || "var(--google-accent)"]),
              ].map(([k, label, color]) => (
                <button key={k} type="button" className="cal-filter" aria-pressed={!calHidden.includes(k)} onClick={() => toggleCalFilter(k)}>
                  <i style={{ background: color }} />{label}
                </button>
              ))}
              {anySchedules && (
                <button type="button" className="cal-filter cal-filter--sched" aria-pressed={showSchedules} onClick={() => setShowSchedules(!showSchedules)}
                  title={showSchedules ? "Hide schedules" : "Show everyone's schedules"}>
                  <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 0113.7-5.6L20 8.5M20 4v4.5h-4.5M20 12a8 8 0 01-13.7 5.6L4 15.5M4 20v-4.5h4.5" /></svg>Schedules
                </button>
              )}
              {googleCalendars.length > 0 && (
                <button type="button" className="cal-filter cal-filter--action" onClick={() => setCalPickerOpen(true)}>
                  <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="3" /><path d="M4 10h16M9 3v4M15 3v4" /></svg>Manage Google calendars
                </button>
              )}
            </div>
          )}

          {/* Which Google calendars to show (saved per account in userSettings) */}
          {calPickerOpen && (
            <Dialog title="Manage Google calendars" onClose={closeCalPicker}>
              <p className="dash-muted" style={{ marginBottom: 12 }}>
                Pick the calendars to show in FriSo. Only you see them, unless you share an event to a pod.
                Hiding a calendar doesn't unshare events you've already shared from it.
              </p>
              <div className="gcal-list">
                {googleCalendars.map((c) => {
                  const shown = isCalShown(c.id);
                  // Hidden, but some of its events are still shared: say so, and offer the list
                  const stillShared = shown ? [] : sharedFromCalendar(c.id);
                  return (
                    <div key={c.id} className="gcal-item">
                    <button type="button" className="gcal-row" role="switch" aria-checked={shown}
                      onClick={() => toggleShownCalendar(c.id)}>
                      <i style={{ background: c.color || "var(--google-accent)" }} />
                      <span className="gcal-row__name">{c.name}{c.primary && <small>Main calendar</small>}</span>
                      <span className="gcal-row__switch" aria-hidden="true" />
                    </button>
                    {stillShared.length > 0 && (
                      <div className="gcal-shared">
                        {stillShared.length === 1 ? "1 event from this calendar is" : `${stillShared.length} events from this calendar are`} still shared with your pods.{" "}
                        <button type="button" className="dash-link" aria-expanded={calPickerExpanded === c.id}
                          onClick={() => setCalPickerExpanded(calPickerExpanded === c.id ? null : c.id)}>
                          {calPickerExpanded === c.id ? "Hide them" : "Show them"}
                        </button>
                        {calPickerExpanded === c.id && (
                          <div className="cal-rows gcal-shared__list">
                            {stillShared.map((ev) => renderDayRow(ev, (e) => { setCalPickerOpen(false); setCalPickerExpanded(null); openEvent(e); }))}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </Dialog>
          )}

          {/* Week view */}
          {effectiveCalView === "week" && renderTimeGrid(weekDays.map((d) => ({ day: d, key: localDateKey(d) })))}

          {/* Day view */}
          {effectiveCalView === "day" && renderTimeGrid([{ day: current, key: localDateKey(current) }])}

          {/* Month view */}
          {effectiveCalView === "month" && (
          <div className="calendar-layout">
            <div className="calendar-main">
          <div className="calendar-grid-header">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="calendar-weekday">
                {d}
              </div>
            ))}
          </div>

          <div className="calendar-body calendar-body--scroll" ref={monthScrollRef} onScroll={onMonthScroll}
            style={monthRowH ? { "--wk-h": `${monthRowH}px` } : undefined}>
            {months.map((mo) => (
            <div key={mo.key} className="cal-month" data-month={mo.key}>
              <div className="cal-month__title">
                {mo.first.toLocaleDateString(undefined, { month: "long" })} <span>{mo.first.getFullYear()}</span>
              </div>
            {mo.weeks.map((week) => {
              const weekKeys = week.map(localDateKey);
              // This month's part of the week (the rest are blank cells)
              const weekStart = weekKeys[0] < mo.startKey ? mo.startKey : weekKeys[0];
              const weekEnd = weekKeys[6] > mo.endKey ? mo.endKey : weekKeys[6];

              const multiDayEvs = allEventsForDisplay.filter((ev) => {
                const start = ev.startDate;
                const end = ev.endDate || start;
                return (
                  start && end && start !== end &&
                  start <= weekEnd && end >= weekStart &&
                  calVisible(ev)
                );
              });

              // Each column = (100% - 24px) / 7, with 4px gaps between 7 columns (.calendar-week gap)
              const colW = "(100% - 24px) / 7";
              const barTop = 33; // px below top of week row (after day number)
              const barH = 20;
              const barGap = 2;
              // Bars are packed into lanes (bars that don't overlap this week share one), and each day only makes room
              // for the lanes that cross it — so a trip on Mon–Tue doesn't push Wed's plans into "+N". Lanes that don't
              // fit under the day number when rows are sized to the pane count in "+N" on their days.
              const clampBar = (ev) => [ev.startDate < weekStart ? weekStart : ev.startDate, (ev.endDate || ev.startDate) > weekEnd ? weekEnd : (ev.endDate || ev.startDate)];
              const laneEnds = [];
              const placedBars = multiDayEvs
                .map((ev) => { const [s, e] = clampBar(ev); return { ev, s, e }; })
                .sort((a, b) => a.s.localeCompare(b.s) || b.e.localeCompare(a.e))
                .map((p) => {
                  let lane = laneEnds.findIndex((end) => end < p.s);
                  if (lane < 0) { lane = laneEnds.length; laneEnds.push(p.e); } else laneEnds[lane] = p.e;
                  return { ...p, lane };
                });
              const lanesFit = monthRowH ? Math.floor((monthRowH - 34) / (barH + barGap)) : Infinity;
              const shownBars = placedBars.filter((p) => p.lane < lanesFit);
              const hiddenBars = placedBars.filter((p) => p.lane >= lanesFit);
              const lanesOn = (key) => Math.max(0, ...shownBars.filter((p) => p.s <= key && p.e >= key).map((p) => p.lane + 1));
              const singleDayOn = (key) => (mergedEvents[key] || []).filter(calVisible).filter((ev) => !ev.endDate || ev.endDate === ev.startDate);
              // A week grows past the fitted row height when a day would otherwise show no plan at all, only "+N"
              const weekH = monthRowH && Math.max(monthRowH, ...weekKeys
                .filter((key) => key >= weekStart && key <= weekEnd && singleDayOn(key).length)
                .map((key) => 36 + lanesOn(key) * (barH + barGap) + 20));

              return (
                <div key={weekStart} className="calendar-week-wrapper" data-week={weekStart} data-week-end={weekEnd}
                  style={weekH > monthRowH ? { height: weekH } : undefined}>
                  {/* Day cells */}
                  <div className="calendar-week">
                    {week.map((day) => {
                      const key = localDateKey(day);
                      if (key < mo.startKey || key > mo.endKey) return <div key={key} className="calendar-cell--blank" aria-hidden="true" />;
                      const isToday = key === todayKey;
                      const todaysEvents = (mergedEvents[key] || [])
                        .filter(calVisible)
                        .filter((ev) => {
                          const start = ev.startDate;
                          const end = ev.endDate || start;
                          return !start || !end || start === end;
                        })
                        .sort((a, b) => {
                          const ta = a.startTime || "00:00";
                          const tb = b.startTime || "00:00";
                          return ta.localeCompare(tb);
                        });
                      // Chips that fit under the day number and any multi-day bars (19px each); else "+N more"
                      // Chips that fit under the day number and the bars (20px each); whatever doesn't fit (chips or
                      // hidden bars) shows as "+N" beside the day number — clicking the day lists everything
                      const lanesHere = lanesOn(key);
                      const chipRoom = monthRowH ? Math.max(0, Math.floor((weekH - 36 - lanesHere * (barH + barGap)) / 20)) : 3;
                      const chipCount = Math.min(todaysEvents.length, chipRoom);
                      const hiddenCount = todaysEvents.length - chipCount + hiddenBars.filter((p) => p.s <= key && p.e >= key).length;
                      return (
                        <div
                          key={key}
                          className={`calendar-cell${isToday ? " calendar-cell__today" : ""}${selectedDay === key && (isMobile || previewOpen) ? " calendar-cell--selected" : ""}${isMobile ? " calendar-cell--mobile" : ""}`}
                          onClick={(e) => {
                            if (isMobile) {
                              setSelectedDay(key);
                              setDaySheetOpen(true);
                            } else {
                              toggleDayPreview(key, e.currentTarget);
                            }
                          }}
                          onDoubleClick={(e) => {
                            // Desktop: double-click a day (not an event in it) to add an event on that day
                            if (isMobile || e.target.closest(".cal-event")) return;
                            setPreviewOpen(false);
                            setStartDateValue(key);
                            setEndDateValue(key);
                            setShowAddEventModal(true);
                          }}
                          onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, dateKey: key }); }}
                        >
                          <div className="calendar-cell__day">
                            <span className="calendar-cell__num">{day.getDate()}</span>
                            {!isMobile && hiddenCount > 0 && <span className="calendar-cell__more">+{hiddenCount}</span>}
                          </div>
                          {isMobile ? (
                            // Mobile: dots only — mergedEvents[key] already covers multi-day spans
                            <div className="cal-cell-dots">
                              {dayEventsFor(key).slice(0, 3).map((ev) => (
                                <span key={ev.id} className="cal-cell-dot" style={{ background: evColors(ev).dot }} />
                              ))}
                            </div>
                          ) : (
                            <>
                              {/* Room for the multi-day bars that cross this day */}
                              {lanesHere > 0 && (
                                <div style={{ height: lanesHere * (barH + barGap) }} />
                              )}
                              {todaysEvents.slice(0, chipCount).map((ev) => {
                                const kind = getEventKind(ev);
                                return (
                                  <button
                                    key={ev.id}
                                    type="button"
                                    className={`cal-event${kind === "busy" ? " cal-event--busy" : ""}`}
                                    onClick={(e) => { e.stopPropagation(); openEvent(ev, e.currentTarget); }}
                                    title={ev.title}
                                  >
                                    {kind !== "busy" && <i style={{ background: evColors(ev).dot }} />}
                                    {ev.startTime && <span className="cal-event__time">{shortTime(ev.startTime)}</span>}
                                    <span className="cal-event__title">{ev.title}</span>
                                  </button>
                                );
                              })}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Multi-day event bars — absolutely positioned to span across cells */}
                  {shownBars.map(({ ev, lane }) => {
                    const evStart = ev.startDate;
                    const evEnd = ev.endDate || evStart;
                    const clampedStart = evStart < weekStart ? weekStart : evStart;
                    const clampedEnd = evEnd > weekEnd ? weekEnd : evEnd;
                    const colStart = weekKeys.indexOf(clampedStart); // 0-indexed
                    const colEnd = weekKeys.indexOf(clampedEnd);     // 0-indexed
                    const spanCols = colEnd - colStart + 1;
                    const isEvStart = evStart >= weekStart;
                    const isEvEnd = evEnd <= weekEnd;
                    const col = evColors(ev);
                    const top = barTop + lane * (barH + barGap);
                    const left = colStart === 0
                      ? "0px"
                      : `calc(${colStart} * (${colW} + 4px))`;
                    const width = spanCols === 7
                      ? "100%"
                      : `calc(${spanCols} * (${colW}) + ${spanCols - 1} * 4px)`;
                    return (
                      <div
                        key={ev.id}
                        className="cal-event cal-event--multiday"
                        style={{
                          position: "absolute",
                          top,
                          left,
                          width,
                          height: barH,
                          margin: 0,
                          background: col.bg,
                          color: col.text,
                          borderTopLeftRadius: isEvStart ? 999 : 0,
                          borderBottomLeftRadius: isEvStart ? 999 : 0,
                          borderTopRightRadius: isEvEnd ? 999 : 0,
                          borderBottomRightRadius: isEvEnd ? 999 : 0,
                        }}
                        onClick={(e) => { e.stopPropagation(); openEvent(ev, e.currentTarget); }}
                        title={ev.title}
                      >
                        {getEventKind(ev) === "traveling" ? "Away · " : ""}
                        {isEvStart && ev.startTime ? `${shortTime(ev.startTime)} ` : ""}
                        {ev.title}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            </div>
            ))}
          </div>
          </div>{/* end calendar-main */}

          {/* Day popover (desktop) — floats beside the clicked cell so the grid never shifts */}
          {previewOpen && !isMobile && createPortal(
            <div ref={previewRef} className="calendar-preview" role="dialog">
              <div className="calendar-preview__header">
                <span className="calendar-preview__date">{new Date(selectedDay + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
                <div className="calendar-preview__actions">
                  <button type="button" className="btn btn--secondary btn--sm pg-btn" onClick={() => { setStartDateValue(selectedDay); setEndDateValue(selectedDay); setPreviewOpen(false); setShowAddEventModal(true); }}>+ Add</button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPreviewOpen(false)} aria-label="Close">✕</button>
                </div>
              </div>
              {dayEventsFor(selectedDay).length === 0
                ? <div className="calendar-preview__empty">Nothing planned</div>
                : <div className="cal-rows">{dayEventsFor(selectedDay).map((ev) => renderDayRow(ev, openEvent))}</div>}
              {renderScheduleLines(selectedDay)}
            </div>,
            document.querySelector(".app-shell") || document.body
          )}
          </div>
          )}

        </>
      )}

      {/* Phone: tapping a day slides its plans up from the bottom (swipe down or tap outside closes it) */}
      {daySheetOpen && isMobile && view === "calendar" && createPortal(
        <div className="modal-overlay" onClick={() => setDaySheetOpen(false)} onTouchMove={(e) => e.stopPropagation()}>
          <div className="modal cal-daysheet" role="dialog" aria-label="Plans for the selected day" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header cal-agenda__head">
              <b>{selectedDay === localDateKey(new Date()) ? "Today" : new Date(selectedDay + "T00:00").toLocaleDateString(undefined, { weekday: "long" })}</b>
              <span>{new Date(selectedDay + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              <button type="button" className="btn btn--secondary btn--sm pg-btn" onClick={() => { setDaySheetOpen(false); setStartDateValue(selectedDay); setEndDateValue(selectedDay); setShowAddEventModal(true); }}>+ Add</button>
            </div>
            <div className="modal__content">
              {dayEventsFor(selectedDay).length === 0
                ? <div className="cal-agenda__free">Nothing planned</div>
                : <div className="cal-rows">{dayEventsFor(selectedDay).map((ev) => renderDayRow(ev, (e) => { setDaySheetOpen(false); openEvent(e); }))}</div>}
              {renderScheduleLines(selectedDay)}
            </div>
          </div>
        </div>,
        document.querySelector(".app-shell") || document.body
      )}

      {/* Add Event modal — shared EventForm (EventForm.js) */}
      {showAddEventModal && createPortal(
        <div className="modal-overlay" onClick={closeAddEvent} onTouchMove={(e) => e.stopPropagation()}>
          <div className="modal modal--form" onClick={(e) => e.stopPropagation()}>
            <EventForm
              key="add"
              mode="add"
              autoFocus={!isMobile}
              pods={pods}
              values={{ title, kind: eventKind, startDate: startDateValue, endDate: endDateValue, startTime: startTimeValue, endTime: endTimeValue, notes, location, pods: selectedEventPods, repeat }}
              onChange={(p) => {
                if ("title" in p) setTitle(p.title);
                if ("kind" in p) setEventKind(p.kind);
                if ("startDate" in p) setStartDateValue(p.startDate);
                if ("endDate" in p) setEndDateValue(p.endDate);
                if ("startTime" in p) setStartTimeValue(p.startTime);
                if ("endTime" in p) setEndTimeValue(p.endTime);
                if ("notes" in p) setNotes(p.notes);
                if ("location" in p) setLocation(p.location);
                if ("pods" in p) setSelectedEventPods(p.pods);
                if ("repeat" in p) setRepeat(p.repeat);
              }}
              onFill={quickFill}
              filling={quickLoading}
              onSave={addEvent}
              onClose={closeAddEvent}
              footerStart={googleAccessToken && (
                <GoogleSyncControl
                  label="Add to Google Calendar"
                  canWrite={googleCanWrite}
                  checked={syncToGoogle}
                  onChange={setSyncToGoogle}
                  onReconnect={onReconnectGoogle}
                />
              )}
            />
          </div>
        </div>,
      document.querySelector('.app-shell') || document.body
      )}

      {/* ── Pods tab (Pods.js) ── */}
      {view === "pods" && (
        <Pods
          podIntent={podIntent}
          onIntentDone={onIntentDone}
          pods={pods}
          invitations={invitations}
          pendingInvitations={pendingInvitations}
          onAcceptInvitation={onAcceptInvitation}
          onDeclineInvitation={onDeclineInvitation}
          contacts={visibleContacts}
          typeaheadContacts={allVisibleContacts}
          currentUserUid={currentUserUid}
          currentUserEmail={currentUserEmail}
          isPodOwner={isPodOwner}
          allEventsForDisplay={allEventsForDisplay}
          isEventVisibleToUser={isEventVisibleToUser}
          onCreatePod={createPod}
          onInviteMember={inviteMember}
          onRemoveMember={removeMemberFromPod}
          onLeavePod={leavePod}
          onRenamePod={renamePod}
          onDeletePod={deletePod}
          onPickPodColor={pickPodColor}
          onPlanWithPod={planWithPod}
          onOpenEvent={openEvent}
          onOpenNote={onOpenNote}
        />
      )}

      {/* ── Contacts tab (Contacts.js) ── */}
      {view === "contacts" && (
        <Contacts
          contacts={visibleContacts}
          pods={pods}
          invitations={invitations}
          currentUserEmail={currentUserEmail}
          isPodOwner={isPodOwner}
          allEventsForDisplay={allEventsForDisplay}
          isEventVisibleToUser={isEventVisibleToUser}
          onCreateContact={createContact}
          onUpdateContact={updateContact}
          onDeleteContact={deleteContact}
          onInviteMember={inviteMember}
          onOpenEvent={openEvent}
        />
      )}

      {/* ── Dashboard tab ── */}
      {view === "stats" && (
        <Dashboard
          allEventsForDisplay={allEventsForDisplay}
          allEventsFlat={allEventsFlat}
          mergedEvents={mergedEvents}
          isEventVisibleToUser={isEventVisibleToUser}
          isEventOwner={isEventOwner}
          currentUserUid={currentUserUid}
          currentUserEmail={currentUserEmail}
          pods={pods}
          contacts={contacts}
          pendingInvitations={pendingInvitations}
          onAcceptInvitation={onAcceptInvitation}
          onDeclineInvitation={onDeclineInvitation}
          onNavigate={onNavigate}
          onOpenNote={onOpenNote}
          onOpenEvent={openEvent}
          onAddEvent={() => setShowAddEventModal(true)}
          onPlanWithPod={planWithPod}
          schedules={mySchedules}
          onAddSchedule={() => setScheduleDraft({})}
          onEditSchedule={setScheduleDraft}
          onToggleSchedule={toggleSchedulePause}
          onPickSlot={(s, podId) => {
            setStartDateValue(s.k);
            setEndDateValue(s.k);
            setStartTimeValue(s.start);
            setEndTimeValue(s.end);
            setEventKind("event");
            setSelectedEventPods([podId]);
            setShowAddEventModal(true);
          }}
        />
      )}

      {scheduleDraft && (
        <ScheduleForm
          key={scheduleDraft.id || "new"}
          schedule={scheduleDraft}
          pods={pods}
          canGoogle={!!googleAccessToken && googleCanWrite !== false}
          googleOn={!!scheduleDraft.syncGoogle}
          onSave={saveSchedule}
          onDelete={deleteSchedule}
          onClose={() => setScheduleDraft(null)}
        />
      )}

      {/* ── Event modal ── */}
      {selectedEvent && createPortal(
        // Mobile: centered modal over a dimmed overlay. Desktop: popover beside the clicked event (no overlay).
        <div className={isMobile ? "modal-overlay" : "event-popover-host"} onClick={isMobile ? closeEvent : undefined} onTouchMove={(e) => e.stopPropagation()}>
          <div ref={eventPopRef} className={`modal${isMobile ? "" : " event-popover"}${editing ? " modal--form" : ""}`} role="dialog" onClick={(e) => e.stopPropagation()}>
            {repeatScope ? (
              // A repeat of a repeating event: which ones does this save / delete apply to?
              <div className="modal__content evc rpt-scope">
                <div className="evc-title">{repeatScope === "save" ? "Change which events?" : "Delete which events?"}</div>
                {[["one", "Just this one", new Date(selectedEvent._occ + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })],
                  ["following", "This and the ones after", ""],
                  ["all", "All of them", ""]].map(([k, label, sub]) => (
                  <button key={k} type="button" className="rpt-scope__opt" onClick={() => (repeatScope === "save" ? saveEdit(k) : deleteEvent(k))}>
                    {label}{sub && <small>{sub}</small>}
                  </button>
                ))}
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setRepeatScope(null)}>Cancel</button>
              </div>
            ) : !editing ? (
              <div className="modal__content evc">
                {(() => {
                  const ev = selectedEvent;
                  const kind = getEventKind(ev);
                  const isG = ev._source === "google";
                  const kicker = isG ? "Google Calendar" : kind === "busy" ? "Busy" : kind === "traveling" ? "Away" : "Event";
                  const ownerName = !isG && !isEventOwner(ev) && ev.ownerEmail && (() => {
                    const c = Object.values(contacts).find((ct) => ct.email === ev.ownerEmail);
                    return (c && [c.firstName, c.lastName].filter(Boolean).join(" ")) || ev.ownerEmail;
                  })();
                  const sd = ev.startDate, ed = ev.endDate && ev.endDate !== ev.startDate ? ev.endDate : null;
                  const dayLabel = (d, opts) => (d === localDateKey(new Date()) ? "Today, " : "") + new Date(d + "T00:00").toLocaleDateString(undefined, opts);
                  const times = ev.startTime ? `${formatTime(ev.startTime)}${ev.endTime ? ` – ${formatTime(ev.endTime)}` : ""}` : "All day";
                  const nDays = ed ? Math.round((new Date(ed + "T00:00") - new Date(sd + "T00:00")) / 86400000) + 1 : 1;
                  const evPods = (ev.sharedPods || []).map((id) => pods[id] && { id, name: pods[id].name }).filter(Boolean);
                  // RSVPs live on the series doc; read the latest snapshot so an answer shows at once
                  const series = seriesOf(ev);
                  const live = allEventsFlat.find((e) => e.id === series.id) || series;
                  const rsvpOn = takesRsvp(ev) && evPods.length > 0;
                  const canAnswer = rsvpOn && !isEventOwner(ev) && (live.memberEmails || []).includes(currentUserEmail) && !!ev.ownerEmail?.includes("@");
                  const answer = live.rsvp?.[currentUserEmail] || null;
                  const onAnswer = async (a) => {
                    setRsvpBusy(true);
                    try {
                      await sendRsvp({ eventId: series.id, email: currentUserEmail, answer: a, prev: answer, hostEmail: live.ownerEmail, title: live.title, startDate: live.startDate });
                    } catch (err) {
                      console.error("RSVP failed", err);
                      alert("Couldn't save your answer. Please try again.");
                    }
                    setRsvpBusy(false);
                  };
                  return (
                    <>
                      <div className="evc-head">
                        <div className="evc-head__text">
                          <span className="dash-eyebrow">{kicker}</span>
                          <div className="evc-title">{ev.title}</div>
                          {/* Creator line sits under the title so long names/emails don't crowd the header */}
                          {ownerName && <div className="evc-by">by {ownerName}</div>}
                        </div>
                        <div className="evc-head__actions">
                          {/* Someone else's event: hiding it is about your FriSo calendar, so it lives here, away from the Google switch */}
                          {!isG && !isEventOwner(ev) && (
                            <span className="pg-menuwrap">
                              <button type="button" className="pg-iconbtn" aria-label="More" aria-expanded={evMenuOpen} onClick={() => setEvMenuOpen(!evMenuOpen)}>
                                <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1.3" /><circle cx="12" cy="12" r="1.3" /><circle cx="19" cy="12" r="1.3" /></svg>
                              </button>
                              {evMenuOpen && (
                                <span className="pg-menu pg-menu--end" role="menu">
                                  <button type="button" role="menuitem" onClick={() => { setEvMenuOpen(false); deleteEvent(); }}>
                                    <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18M10.6 6.1A9.6 9.6 0 0112 6c5 0 9 6 9 6a15.7 15.7 0 01-2.9 3.4M6.6 7.6C4.3 9.1 3 12 3 12s4 6 9 6a8.9 8.9 0 004.3-1.1" /></svg>
                                    <span>Hide from my calendar<small>It stays on the pod for everyone else</small></span>
                                  </button>
                                </span>
                              )}
                            </span>
                          )}
                          <button type="button" className="pg-iconbtn" aria-label="Close" onClick={closeEvent}>✕</button>
                        </div>
                      </div>
                      {canAnswer && <RsvpAsk answer={answer} onAnswer={onAnswer} busy={rsvpBusy} />}
                      <div className="evc-rows">
                        {sd && (
                          <div className="evc-row">
                            <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></svg>
                            <div>
                              {ed
                                ? `${dayLabel(sd, { weekday: "short", month: "short", day: "numeric" })} – ${new Date(ed + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`
                                : dayLabel(sd, { weekday: "long", month: "short", day: "numeric" })}
                              <small>{times}{ed && !ev.startTime ? ` · ${nDays} days` : ""}</small>
                            </div>
                          </div>
                        )}
                        {ev.repeat && (
                          <div className="evc-row">
                            <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 0113.7-5.6L20 8.5M20 4v4.5h-4.5M20 12a8 8 0 01-13.7 5.6L4 15.5M4 20v-4.5h4.5" /></svg>
                            <div>{repeatLabel(ev.repeat, seriesOf(ev).startDate)}{ev.schedule && <small>{ev.paused ? "Schedule · paused" : "Part of a schedule"}</small>}</div>
                          </div>
                        )}
                        {ev.location && (
                          <div className="evc-row">
                            <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0113 0c0 5-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></svg>
                            <div className="evc-pre">{ev.location}</div>
                          </div>
                        )}
                        <div className="evc-row">
                          <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="9" r="3.2" /><path d="M3.5 19c.9-3.2 3-4.6 5.5-4.6s4.6 1.4 5.5 4.6" /><path d="M15.5 6.2a3 3 0 010 5.6M17.5 14.6c1.6.5 2.6 1.9 3 4.4" /></svg>
                          {rsvpOn ? (
                            <RsvpPods ev={live} pods={pods} podColor={getPodColor} nameOf={nameOf} initialsOf={initialsOf} me={currentUserEmail} />
                          ) : evPods.length ? (
                            <div className="pg-chips">
                              {evPods.map((p) => {
                                const c = getPodColor(p.id);
                                return <span key={p.id} className="pg-podchip" style={{ background: c.bg, color: c.text }}>{p.name}</span>;
                              })}
                            </div>
                          ) : (
                            <div className="evc-muted">{isG ? `From your Google calendar${ev._calendarName ? ` · ${ev._calendarName}` : ""}` : "Only you"}</div>
                          )}
                        </div>
                      </div>
                      {ev.notes && <div className="evc-notes">{ev.notes}</div>}
                      {ev.googleEventId && isEventOwner(ev) && googleCalendars.length > 0 && !isCalShown(twinCalendarId(ev)) && (
                        <div className="evc-private">Its Google calendar is hidden. This copy stays with your pods (and keeps updating from Google) until you delete it.</div>
                      )}
                      {kind === "busy" && isEventOwner(ev) && evPods.length > 0 && (
                        <div className="evc-private">Your pods see “Busy” and the time, not the title, place or notes.</div>
                      )}
                    </>
                  );
                })()}

                {(selectedEvent.eventType === "fromGmail" || selectedEvent.googleEventId) && selectedEvent.htmlLink && (selectedEvent._source === "google" || isEventOwner(selectedEvent)) && (
                  <div style={{ marginTop: 8, textAlign: "right" }}>
                    <a href={selectedEvent.htmlLink} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 13, color: "var(--google-text)", textDecoration: "none" }}>
                      View in Google Calendar ↗
                    </a>
                  </div>
                )}

                {/* Share section — only for unshared Google events owned by this user */}
                {selectedEvent._source === "google" && (selectedEvent.ownerUid === currentUserUid || selectedEvent.ownerEmail === currentUserEmail) && (
                  <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                    {!showGoogleShareForm ? (
                      <button className="btn btn--primary btn--sm" onClick={() => setShowGoogleShareForm(true)}>
                        Edit
                      </button>
                    ) : (
                      <div style={{ width: "100%" }}>
                        <div className="event-type-toggle" style={{ marginBottom: 10 }}>
                          <button type="button" className={`event-type-toggle__btn${googleShareKind === "event" ? " event-type-toggle__btn--active" : ""}`} onClick={() => setGoogleShareKind("event")}>Event</button>
                          <button type="button" className={`event-type-toggle__btn${googleShareKind === "busy" ? " event-type-toggle__btn--active event-type-toggle__btn--busy" : ""}`} onClick={() => setGoogleShareKind("busy")}>Busy</button>
                          <button type="button" className={`event-type-toggle__btn${googleShareKind === "traveling" ? " event-type-toggle__btn--active event-type-toggle__btn--traveling" : ""}`} onClick={() => setGoogleShareKind("traveling")}>✈ Traveling</button>
                        </div>
                        {googleShareKind === "busy" && <div className="ef-hint" style={{ marginTop: -4, marginBottom: 10 }}>Your pods see “Busy” and the time, not what it is.</div>}
                        {Object.keys(pods).length === 0 ? (
                          <div style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 10 }}>
                            You have no pods yet. Create one in the Pods tab.
                          </div>
                        ) : (
                          <PodBubblePicker
                            pods={Object.values(pods)}
                            selected={googleSharePods}
                            onToggle={(id) => setGoogleSharePods((prev) =>
                              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                            )}
                          />
                        )}
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                          <button className="btn btn--ghost btn--sm" onClick={() => setShowGoogleShareForm(false)}>Cancel</button>
                          <button
                            className="btn btn--primary btn--sm"
                            onClick={shareGoogleEvent}
                            disabled={googleSharePods.length === 0}
                          >
                            Share
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedEvent._source !== "google" && (
                  <div className="evc-foot">
                    <div className="ef-foot-start">
                      {googleAccessToken && canSyncToGoogle(selectedEvent) && (
                        <GoogleSyncControl
                          label="In Google Calendar"
                          canWrite={googleCanWrite}
                          checked={!!googleCopies?.[seriesOf(selectedEvent).id]}
                          disabled={!googleCopies}
                          onChange={(on) => toggleGoogleCopy(seriesOf(selectedEvent), on)}
                          onReconnect={onReconnectGoogle}
                        />
                      )}
                    </div>
                    {isEventOwner(selectedEvent) && (
                      <div className="evc-foot__actions">
                        <button type="button" className="ef-trash" onClick={() => deleteEvent()} aria-label="Delete event" title="Delete event"><TrashIcon /></button>
                        {selectedEvent.schedule
                          ? <button type="button" className="btn btn--primary pg-btn" onClick={() => { const sch = seriesOf(selectedEvent); closeEvent(); setScheduleDraft(sch); }}>Edit schedule</button>
                          : <button type="button" className="btn btn--primary pg-btn" onClick={() => setEditing(true)}>Edit</button>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <EventForm
                key={selectedEvent.id}
                mode="edit"
                pods={pods}
                lockedFromGoogle={!!selectedEvent.googleEventId}
                values={{ title: editTitle, kind: editEventKind, startDate: editStartDateValue, endDate: editEndDateValue, startTime: editStartTimeValue, endTime: editEndTimeValue, notes: editNotes, location: editLocation, pods: editEventPods, repeat: editRepeat }}
                onChange={(p) => {
                  if ("title" in p) setEditTitle(p.title);
                  if ("kind" in p) setEditEventKind(p.kind);
                  if ("startDate" in p) setEditStartDateValue(p.startDate);
                  if ("endDate" in p) setEditEndDateValue(p.endDate);
                  if ("startTime" in p) setEditStartTimeValue(p.startTime);
                  if ("endTime" in p) setEditEndTimeValue(p.endTime);
                  if ("notes" in p) setEditNotes(p.notes);
                  if ("location" in p) setEditLocation(p.location);
                  if ("pods" in p) setEditEventPods(p.pods);
                  if ("repeat" in p) setEditRepeat(p.repeat);
                }}
                onSave={() => saveEdit()}
                onClose={() => setEditing(false)}
                onDelete={() => deleteEvent()}
                footerStart={googleAccessToken && canSyncToGoogle(selectedEvent) && (
                  <GoogleSyncControl
                    label="In Google Calendar"
                    canWrite={googleCanWrite}
                    checked={!!googleCopies?.[seriesOf(selectedEvent).id]}
                    disabled={!googleCopies}
                    onChange={(on) => toggleGoogleCopy(seriesOf(selectedEvent), on)}
                    onReconnect={onReconnectGoogle}
                  />
                )}
              />
            )}
          </div>
        </div>,
      document.querySelector('.app-shell') || document.body
      )}

      {/* ── Right-click context menu ── */}
      {contextMenu && (
        <>
          <div className="cal-context-backdrop" onClick={() => setContextMenu(null)} />
          <div
            className="cal-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button
              className="cal-context-menu__item"
              onClick={() => {
                setStartDateValue(contextMenu.dateKey);
                setEndDateValue(contextMenu.dateKey);
                if (contextMenu.startTime) setStartTimeValue(contextMenu.startTime);
                if (contextMenu.endTime) setEndTimeValue(contextMenu.endTime);
                setShowAddEventModal(true);
                setContextMenu(null);
              }}
            >
              Add Event
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

function PodBubblePicker({ pods, selected, onToggle }) {
  return (
    <div className="pod-bubbles">
      {pods.map((pod) => {
        const isSelected = selected.includes(pod.id);
        const c = getPodColor(pod.id);
        return (
          <button
            key={pod.id}
            type="button"
            className={`pod-bubble ${isSelected ? "pod-bubble--selected" : ""}`}
            onClick={() => onToggle(pod.id)}
          >
            <div
              className="pod-bubble__avatar"
              style={isSelected
                ? { background: c.bg, color: "var(--color-text)", border: `3px solid ${c.border}`, boxShadow: `0 0 0 2px ${c.border}` }
                : { background: "var(--color-bg)", color: "var(--color-text-muted)", border: "2px solid var(--color-border)" }
              }
            >
              {getInitials(pod.name)}
            </div>
            <div className="pod-bubble__name" style={isSelected ? { color: "var(--color-text)", fontWeight: 700 } : undefined}>{pod.name}</div>
          </button>
        );
      })}
    </div>
  );
}

// "Sync to Google Calendar" checkbox, or a reconnect prompt when the token can't write yet.
function GoogleSyncControl({ canWrite, checked, disabled, onChange, onReconnect, label = "Sync to Google" }) {
  const [busy, setBusy] = useState(false);
  if (canWrite === false) {
    return (
      <button type="button" className="btn btn--ghost btn--sm" style={{ whiteSpace: "nowrap" }} onClick={onReconnect}
        title="Reconnect Google Calendar to sync events to it">
        Reconnect Google
      </button>
    );
  }
  // The knob is a real <span>: iOS (WebKit) doesn't draw ::after on <input>, so a pseudo-element knob
  // vanished on iPhones and the switch looked dead.
  return (
    <label className="gsync" title="Keep a copy of this event in your Google Calendar">
      <input
        type="checkbox"
        className="gsync__input"
        checked={checked}
        disabled={disabled || busy || canWrite === null}
        onChange={async (e) => {
          setBusy(true);
          await onChange(e.target.checked);
          setBusy(false);
        }}
      />
      <span className="gsync__track" aria-hidden="true" />
      {label}
    </label>
  );
}
